// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import FileUpload from './components/FileUpload';
import ComparativeFileUpload from './components/ComparativeFileUpload';
import PeriodSelectorMulti from './components/PeriodSelector/PeriodSelectorMulti';
import ComparativeSummary from './components/ComparativeSummary';
import ResultsDisplayMulti from './components/ResultsDisplay/ResultsDisplayMulti';
// ComparativeResultsDisplay removed - using ResultsDisplayMulti with dataFormat="comparative"
import ChartOfAccountsEditor from './components/ChartOfAccountsEditor';
import AccountOverview from './components/AccountOverview';
import QoEWorkbench from './components/QoEWorkbench/QoEWorkbench';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import MergerUpload from './components/MergerUpload';
import MergerPeriodSelector from './components/MergerPeriodSelector';
import ReconUpload from './components/ReconUpload';
import { TBConverterUpload, TBConverterPeriodReview, TBConverterClassification } from './components/TBConverter';
import Agent from './components/Agent';
import BalanceSheetAgent from './components/Agent/BalanceSheetAgent';
import TrialBalanceToBalanceSheetAgent from './components/Agent/TrialBalanceToBalanceSheetAgent';
import UnifiedAgent from './components/Agent/UnifiedAgent';
import DatabookAgent from './components/Agent/DatabookAgent';
import { TenantProvider, useTenantConfig } from './contexts';
import { supabase } from './utils/supabaseClient';
import SessionExpiredModal from './components/SessionExpiredModal';
import logger from './utils/logger';
import { saveSession, listSessions, getSession, deleteSession, pinSession, unpinSession, attachQoeReport, updateSessionResults } from './utils/sessionsApi';
import { listAgentSessions, deleteAgentSession } from './utils/agentApi';
import { getSessionStatus } from './utils/authApi';
import { SessionExpiredError } from './utils/sessionExpiredError';
import axios from 'axios';
import './App.css';

// Keys that this app owns - all others will be cleaned up on load
// (except Supabase keys which start with 'sb-')
const APP_OWNED_KEYS = ['darkMode', 'llmProvider'];

// ---------------------------------------------------------------------------
// Global fetch interceptor — automatically injects the X-LLM-Provider header
// into every request to our backend.  This means ZERO changes to existing
// fetch calls in child components.
// Also detects 401 responses (session expired) and dispatches a custom event
// so the app can show a re-auth modal without losing state.
// ---------------------------------------------------------------------------
const _originalFetch = window.fetch;

// Auth endpoints should not trigger session-expired (they ARE the re-auth flow,
// or the cold-mount auth probe whose 401 means "show login", not "session died").
const AUTH_ENDPOINTS = ['/auth/set-cookie-token', '/auth/logout', '/auth/session-status'];

window.fetch = function (url, options = {}) {
  const isBackendCall =
    typeof url === 'string' &&
    import.meta.env.VITE_BACKEND_BASE_URL &&
    url.startsWith(import.meta.env.VITE_BACKEND_BASE_URL);

  if (isBackendCall) {
    const provider = localStorage.getItem('llmProvider') || 'openai';
    // Merge header — works with both Headers objects and plain objects
    if (options.headers instanceof Headers) {
      if (!options.headers.has('X-LLM-Provider')) {
        options.headers.set('X-LLM-Provider', provider);
      }
    } else {
      options.headers = {
        ...options.headers,
        'X-LLM-Provider': provider,
      };
    }
  }

  return _originalFetch.call(this, url, options).then((response) => {
    // Detect 401 on backend calls (excluding auth endpoints themselves)
    if (
      isBackendCall &&
      response.status === 401 &&
      !AUTH_ENDPOINTS.some((ep) => url.includes(ep))
    ) {
      window.dispatchEvent(new CustomEvent('session-expired'));
      // Reject so calling code stops processing instead of continuing with the 401 response
      throw new SessionExpiredError();
    }
    return response;
  });
};

// ---------------------------------------------------------------------------
// Global axios interceptor — detects 401 responses from backend and
// dispatches the same 'session-expired' event for the re-auth modal.
// ---------------------------------------------------------------------------
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      error.config?.url &&
      import.meta.env.VITE_BACKEND_BASE_URL &&
      error.config.url.startsWith(import.meta.env.VITE_BACKEND_BASE_URL) &&
      !AUTH_ENDPOINTS.some((ep) => error.config.url.includes(ep))
    ) {
      window.dispatchEvent(new CustomEvent('session-expired'));
      // Reject with SessionExpiredError so callers can silently ignore it
      return Promise.reject(new SessionExpiredError());
    }
    return Promise.reject(error);
  }
);

// Gate component — keeps showing a loading screen until tenant features have loaded.
// If the backend is unreachable, shows an error page instead of a partial dashboard.
// This sits inside TenantProvider so it can read context.
function DashboardGate({ children }) {
  const { featuresLoading, featuresError, retryFeatures } = useTenantConfig();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    await retryFeatures();
    setRetrying(false);
  };

  if (featuresLoading || retrying) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>{retrying ? 'Retrying...' : 'Loading your workspace...'}</p>
      </div>
    );
  }

  if (featuresError) {
    return (
      <div className="auth-loading">
        <div className="connection-error">
          <h2>Unable to Connect</h2>
          <p>We couldn't reach the server. Please check your connection and try again.</p>
          <button onClick={handleRetry} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return children;
}

// Component to manage default page based on enabled features
function FeaturePageManager({ activePage, setActivePage, setHasComparativeFeature }) {
  const { featuresLoading, hasFeature } = useTenantConfig();
  
  useEffect(() => {
    // Wait for features to finish loading
    if (featuresLoading) return;
    
    // Update comparative feature flag
    setHasComparativeFeature(hasFeature('comparative'));
    
    // Set default page once features are loaded
    if (activePage === null) {
      // Default to first available tool, or account page if no tools
      if (hasFeature('mapper')) {
        setActivePage('mapper');
      } else if (hasFeature('comparative')) {
        setActivePage('comparative');
      } else if (hasFeature('merger')) {
        setActivePage('merger');
      } else if (hasFeature('recon')) {
        setActivePage('recon');
      } else if (hasFeature('tb_converter')) {
        setActivePage('tb-converter');
      } else if (hasFeature('agent')) {
        setActivePage('agent');
      } else if (hasFeature('databook')) {
        // Databook is gated on its own flag alone, so it can be a tenant's
        // only tool.
        setActivePage('databook');
      } else {
        setActivePage('account');
      }
    } else {
      // If user is on a tool page they don't have access to, redirect
      if (activePage === 'mapper' && !hasFeature('mapper')) {
        // 'mapper' is also where the account page's back button lands after a
        // hard refresh, so a databook-only tenant must resolve to its tool.
        setActivePage(hasFeature('comparative') ? 'comparative' : hasFeature('merger') ? 'merger' : hasFeature('recon') ? 'recon' : hasFeature('tb_converter') ? 'tb-converter' : hasFeature('agent') ? 'agent' : hasFeature('databook') ? 'databook' : 'account');
      } else if (activePage === 'comparative' && !hasFeature('comparative')) {
        setActivePage(hasFeature('mapper') ? 'mapper' : hasFeature('merger') ? 'merger' : hasFeature('recon') ? 'recon' : hasFeature('tb_converter') ? 'tb-converter' : hasFeature('agent') ? 'agent' : 'account');
      } else if (activePage === 'merger' && !hasFeature('merger')) {
        setActivePage(hasFeature('mapper') ? 'mapper' : hasFeature('comparative') ? 'comparative' : hasFeature('recon') ? 'recon' : hasFeature('tb_converter') ? 'tb-converter' : hasFeature('agent') ? 'agent' : 'account');
      } else if (activePage === 'recon' && !hasFeature('recon')) {
        setActivePage(hasFeature('mapper') ? 'mapper' : hasFeature('comparative') ? 'comparative' : hasFeature('merger') ? 'merger' : hasFeature('tb_converter') ? 'tb-converter' : hasFeature('agent') ? 'agent' : 'account');
      } else if (activePage === 'tb-converter' && !hasFeature('tb_converter')) {
        setActivePage(hasFeature('mapper') ? 'mapper' : hasFeature('comparative') ? 'comparative' : hasFeature('merger') ? 'merger' : hasFeature('recon') ? 'recon' : hasFeature('agent') ? 'agent' : 'account');
      } else if (activePage === 'qoe-workbench' && !hasFeature('mapper')) {
        // QoE workbench is gated on mapper access (its data source).
        setActivePage(hasFeature('comparative') ? 'comparative' : hasFeature('merger') ? 'merger' : hasFeature('recon') ? 'recon' : hasFeature('tb_converter') ? 'tb-converter' : hasFeature('agent') ? 'agent' : 'account');
      } else if (activePage === 'balance-sheet' && !hasFeature('balance_sheet')) {
        // Balance Sheet is a separate, layered add-on; never reachable (even by
        // direct #/balance-sheet URL) without the balance_sheet feature.
        setActivePage(hasFeature('agent') ? 'agent' : hasFeature('mapper') ? 'mapper' : 'account');
      } else if (activePage === 'tb-to-bs' && !hasFeature('tb_bs')) {
        // Trial Balance → Balance Sheet is gated strictly on the dedicated
        // tb_bs feature; never reachable (even by direct #/tb-to-bs URL) without it.
        setActivePage(hasFeature('agent') ? 'agent' : hasFeature('mapper') ? 'mapper' : 'account');
      } else if (activePage === 'unified' && !hasFeature('unified_agent')) {
        // Full Statements (unified P&L + BS) is a layered add-on; never
        // reachable (even by direct #/unified URL) without unified_agent.
        setActivePage(hasFeature('agent') ? 'agent' : hasFeature('mapper') ? 'mapper' : 'account');
      } else if (activePage === 'databook' && !hasFeature('databook')) {
        // Databook is gated strictly on the dedicated databook feature; never
        // reachable (even by direct #/databook URL) without it.
        setActivePage(hasFeature('agent') ? 'agent' : hasFeature('mapper') ? 'mapper' : 'account');
      }
    }
  }, [featuresLoading, activePage, hasFeature, setActivePage, setHasComparativeFeature]);
  
  return null; // This component only manages state, renders nothing
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // Loading state for initial auth check
  const [passwordRecoveryMode, setPasswordRecoveryMode] = useState(false); // For password reset flow
  const [step, setStep] = useState(1); // 1: Upload, 2: Period/Entity Selection, 3: Results
  const [fileData, setFileData] = useState(null);
  const [dataFormat, setDataFormat] = useState(null); // 'pl' or 'comparative'
  const [statementType, setStatementType] = useState(null);
  const [periodOptions, setPeriodOptions] = useState(null); // For P&L: period discovery, For Comparative: combined object
  const [results, setResults] = useState(null);
  const resultsRef = useRef(null);
  useEffect(() => { resultsRef.current = results; }, [results]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [netProfitCheck, setNetProfitCheck] = useState(null);
  // Approval state for net-profit-checker suggestion chips. Lifted from
  // ResultsDisplayMulti so save/load can persist it across sessions (BLI-8).
  const [approvedSuggestions, setApprovedSuggestions] = useState(new Set());
  const [rejectedSuggestions, setRejectedSuggestions] = useState(new Set());
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize from localStorage for persistence across page loads
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  const [llmProvider, setLlmProvider] = useState(() => {
    return localStorage.getItem('llmProvider') || 'openai';
  });
  // Initialize from URL hash so a hard refresh of e.g. #/comparative or
  // #/mapper/session/<id> lands the user on the right page instead of the
  // tenant-default. Must reject Supabase auth-flow hashes (#access_token=...,
  // #type=invite) — those are handled by the auth effect, not by routing.
  const [activePage, setActivePage] = useState(() => {
    const h = typeof window !== 'undefined' ? window.location.hash : '';
    if (!h.startsWith('#/')) return null;
    const seg = h.slice(2).split('/')[0];
    const valid = ['mapper', 'merger', 'comparative', 'qoe', 'recon', 'tb-converter', 'chart-of-accounts', 'account', 'agent', 'balance-sheet', 'tb-to-bs', 'unified', 'databook'];
    return valid.includes(seg) ? seg : null;
  }); // 'mapper', 'merger', 'comparative', 'chart-of-accounts', 'account' - null until features load
  // Tracks where the user was before opening the account page, so the
  // account-page back button can restore the previous tool without going
  // through handleSelectPage (which would reset in-progress state).
  const [previousPage, setPreviousPage] = useState(null);
  const [hasComparativeFeature, setHasComparativeFeature] = useState(false); // Comparative feature flag
  
  // Comparative state
  const [comparativeStep, setComparativeStep] = useState(1); // 1: Upload, 2: Summary, 3: Results
  const [comparativeData, setComparativeData] = useState(null); // Discovery data for comparative
  const [comparativeResults, setComparativeResults] = useState(null);
  const [comparativeProgress, setComparativeProgress] = useState(null);
  const [comparativeEbitdaChecks, setComparativeEbitdaChecks] = useState({}); // { sheetName: ebitdaCheckResult }
  const comparativeResultsRef = useRef(null);
  useEffect(() => { comparativeResultsRef.current = comparativeResults; }, [comparativeResults]);

  // Merger state
  const [mergerStep, setMergerStep] = useState(1); // 1: Upload, 2: Period Selection, 3: Results
  const [mergerData, setMergerData] = useState(null); // Discovery result + files + selections
  const [mergerResults, setMergerResults] = useState(null);
  const [mergerProgress, setMergerProgress] = useState(null);
  
  // TB Converter state
  const [tbConverterStep, setTbConverterStep] = useState(1); // 1: Upload, 2: Period Review, 3: Classification, 4: Results
  const [tbConverterData, setTbConverterData] = useState(null); // Analysis result + files + selections
  const [tbClassificationResult, setTbClassificationResult] = useState(null); // Stored classification for persistence
  const [tbConverterResults, setTbConverterResults] = useState(null);
  const [tbConverterProgress, setTbConverterProgress] = useState(null);
  
  // QoE Analysis state - lifted to App level for persistence across tab switches
  const [mapperQoeResults, setMapperQoeResults] = useState(null);
  const [mapperQoeShowPanel, setMapperQoeShowPanel] = useState(false);
  const [mergerQoeResults, setMergerQoeResults] = useState(null);
  const [mergerQoeShowPanel, setMergerQoeShowPanel] = useState(false);
  
  // Clean up stale localStorage keys from other projects that shared localhost:3000
  useEffect(() => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Keep: our app's keys, Supabase auth keys (start with 'sb-')
      if (key && !APP_OWNED_KEYS.includes(key) && !key.startsWith('sb-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);
  const [tenantInfo, setTenantInfo] = useState(null); // Tenant/company info from AccountOverview
  
  // Session expired state — shows re-auth modal overlay without losing app state
  const [sessionExpired, setSessionExpired] = useState(false);

  // Saved mapping sessions (persistence)
  const [savedSessions, setSavedSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loadedSessionId, setLoadedSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false); // Loading a saved session
  const [toast, setToast] = useState(null); // { message, visible }

  // Mapper (P&L) sessions — rolling buffer of 5 recent + pinned
  const [mapperSessions, setMapperSessions] = useState([]);
  const [mapperSessionsLoading, setMapperSessionsLoading] = useState(false);
  // ID of the session row created by auto-save after a mapper run — used to pin / attach QoE later
  const [currentMapperSessionId, setCurrentMapperSessionId] = useState(null);
  // Agent sessions (separate flow with its own /agent/sessions endpoints)
  const [agentSessions, setAgentSessions] = useState([]);
  const [currentAgentSessionId, setCurrentAgentSessionId] = useState(null);
  // Isolated balance-sheet flow: its own current-session id, parallel to the
  // agent (P&L) one. Both flows draw from the same agentSessions list, split
  // by file_refs[0].statement_type.
  const [currentBalanceSheetSessionId, setCurrentBalanceSheetSessionId] = useState(null);
  // Isolated TB→BS flow: its own current-session id, parallel to the BS and
  // agent (P&L) flows. Same agentSessions list, split by
  // file_refs[0].statement_type === 'tb_bs'.
  const [currentTBToBSSessionId, setCurrentTBToBSSessionId] = useState(null);
  // Unified (P&L + BS) flow: the PARENT session id. Parents are agent
  // sessions too, marked file_refs[0].session_kind === 'unified'; their two
  // child sessions are hidden from the sidebar lists.
  const [currentUnifiedSessionId, setCurrentUnifiedSessionId] = useState(null);
  // Databook flow: the PARENT session id, as for unified. Parents are marked
  // file_refs[0].session_kind === 'databook'; their two child sessions are
  // hidden from the sidebar lists.
  const [currentDatabookSessionId, setCurrentDatabookSessionId] = useState(null);
  // True when user arrived at mapper results by loading a saved session.
  // Suppresses the 1-2-3 stepper and "Back to Periods" (no file/periods are in memory).
  const [isViewingSavedMapperSession, setIsViewingSavedMapperSession] = useState(false);
  // ISO timestamp shown in the mapper results header ("Mapping · <date>").
  // Set on mapper stream completion (live run) or on session load (saved run).
  const [mapperProcessedAt, setMapperProcessedAt] = useState(null);
  // Original source P&L filename from a loaded session — used as a hint in the re-upload modal.
  const [mapperSourceFileName, setMapperSourceFileName] = useState(null);

  // Use ref to persist recovery mode across auth state changes
  const recoveryModeRef = useRef(false);
  
  // Track last cookie token to prevent duplicate set-cookie calls
  const lastCookieTokenRef = useRef(null);
  
  // Helper to set session cookie (prevents duplicates)
  const setSessionCookie = async (token) => {
    // Skip if we already set this exact token
    if (lastCookieTokenRef.current === token) {
      return true;
    }
    try {
      await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/auth/set-cookie-token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
        body: JSON.stringify({ token }),
      });
      lastCookieTokenRef.current = token;
      return true;
    } catch (err) {
      logger.error('Failed to set session cookie:', err);
      return false;
    }
  };
  
  // Handle dark mode toggle - simple color inversion
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
    // Persist preference to localStorage
    localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);
  
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Persist LLM provider choice — the global fetch interceptor reads from localStorage
  const handleProviderChange = (e) => {
    const provider = e.target.value;
    setLlmProvider(provider);
    localStorage.setItem('llmProvider', provider);
  };

  // --- Saved sessions handlers ---
  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await listSessions('comparative');
      setSavedSessions(data.sessions || []);
    } catch (err) {
      logger.error('Failed to fetch sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const saveComparativeSession = useCallback(async (resultsObj, customName) => {
    if (!resultsObj) return;
    try {
      // Extract only persistable keys (NOT source_file_base64 or comparable_metadata)
      const { accounts, accountsByEntity, period_identifier, totalPeriods, statement_type, company_context } = resultsObj;

      // Count ERP vs comparable entities from actual source_type on accounts
      const erpEntities = new Set();
      const compEntities = new Set();
      (accounts || []).forEach(a => {
        if (a.source_type === 'comparable') compEntities.add(a.entity);
        else erpEntities.add(a.entity);
      });

      const allEntityNames = Object.keys(accountsByEntity || {});
      const name = customName || `${allEntityNames.slice(0, 3).join(', ')}${allEntityNames.length > 3 ? '...' : ''} — ${new Date().toLocaleDateString()}`;

      // BLI-8: persist NPC payload + approval state so saved sessions can
      // restore the breakdown panel + balancing banner.
      const npcPayload = netProfitCheck || null;
      const npcApprovals = (approvedSuggestions.size || rejectedSuggestions.size)
        ? { approved: [...approvedSuggestions], rejected: [...rejectedSuggestions] }
        : null;

      await saveSession({
        name,
        feature_key: 'comparative',
        entity_count: erpEntities.size,
        account_count: (accounts || []).length,
        period_count: totalPeriods || 0,
        comparable_count: compEntities.size,
        results_data: {
          accounts, accountsByEntity, period_identifier, totalPeriods, statement_type, company_context,
          net_profit_check: npcPayload,
          npc_approvals: npcApprovals,
        },
        processing_config: {},
      });

      fetchSessions();
    } catch (err) {
      logger.error('Failed to auto-save session:', err);
    }
  }, [fetchSessions, netProfitCheck, approvedSuggestions, rejectedSuggestions]);

  const fetchMapperSessions = useCallback(async () => {
    setMapperSessionsLoading(true);
    try {
      const data = await listSessions('mapping');
      setMapperSessions(data.sessions || []);
    } catch (err) {
      logger.error('Failed to fetch mapper sessions:', err);
    } finally {
      setMapperSessionsLoading(false);
    }
  }, []);

  const fetchAgentSessions = useCallback(async () => {
    try {
      const data = await listAgentSessions();
      setAgentSessions(data.sessions || []);
    } catch (err) {
      logger.error('Failed to fetch agent sessions:', err);
    }
  }, []);

  const handleLoadAgentSession = useCallback((sessionId) => {
    setActivePage('agent');
    setCurrentAgentSessionId(sessionId);
  }, []);

  const handleLoadBalanceSheetSession = useCallback((sessionId) => {
    setActivePage('balance-sheet');
    setCurrentBalanceSheetSessionId(sessionId);
  }, []);

  const handleLoadTBToBSSession = useCallback((sessionId) => {
    setActivePage('tb-to-bs');
    setCurrentTBToBSSessionId(sessionId);
  }, []);

  const handleLoadUnifiedSession = useCallback((sessionId) => {
    setActivePage('unified');
    setCurrentUnifiedSessionId(sessionId);
  }, []);

  const handleLoadDatabookSession = useCallback((sessionId) => {
    setActivePage('databook');
    setCurrentDatabookSessionId(sessionId);
  }, []);

  const handleDeleteAgentSession = useCallback(
    async (sessionId) => {
      try {
        await deleteAgentSession(sessionId);
      } catch (err) {
        logger.error('Failed to delete agent session:', err);
        // Refetch anyway in case the row is already gone server-side.
      }
      setAgentSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setCurrentAgentSessionId((prev) => (prev === sessionId ? null : prev));
      fetchAgentSessions();
    },
    [fetchAgentSessions],
  );

  const handleClearAllAgentSessions = useCallback(async () => {
    // Snapshot the list so subsequent state updates don't change what we
    // iterate over. Then delete each one, swallow individual failures so
    // a single 403/404 doesn't strand the rest.
    const snapshot = [...agentSessions];
    await Promise.all(
      snapshot.map(async (s) => {
        try {
          await deleteAgentSession(s.id);
        } catch (err) {
          logger.error(`Failed to delete agent session ${s.id}:`, err);
        }
      }),
    );
    setAgentSessions([]);
    setCurrentAgentSessionId(null);
    fetchAgentSessions();
  }, [agentSessions, fetchAgentSessions]);

  // Keep the agent-session list fresh: refetch whenever the user switches
  // INTO or AWAY FROM the agent page (so newly created sessions appear in
  // the sidebar without a hard refresh).
  const lastAgentPageRef = useRef(false);
  useEffect(() => {
    const onAgent = activePage === 'agent' || activePage === 'balance-sheet' || activePage === 'tb-to-bs' || activePage === 'unified' || activePage === 'databook';
    if (lastAgentPageRef.current !== onAgent) {
      lastAgentPageRef.current = onAgent;
      if (isAuthenticated) fetchAgentSessions();
    }
  }, [activePage, isAuthenticated, fetchAgentSessions]);

  // Poll the agent sessions list every 5s while ANY session is still
  // 'active' so the sidebar status pill flips from running → completed/
  // failed without the user having to refresh. Stops as soon as no
  // active session remains, and pauses when the tab is hidden so a
  // backgrounded tab doesn't burn requests.
  const hasActiveAgentSession = agentSessions.some((s) => s.status === 'active');
  useEffect(() => {
    if (!isAuthenticated || !hasActiveAgentSession) return;
    let cancelled = false;
    const interval = setInterval(() => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchAgentSessions();
    }, 5000);
    // Fire one immediate refresh when the tab regains focus so the user
    // sees fresh status the moment they tab back.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        fetchAgentSessions();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [isAuthenticated, hasActiveAgentSession, fetchAgentSessions]);

  const saveMapperSession = useCallback(async (resultsObj, customName, fileDataSnapshot) => {
    if (!resultsObj) return null;
    try {
      const { accounts, accountsByPeriod, totalPeriods, statement_type, company_context, period_identifier } = resultsObj;
      const name = customName || `Mapping — ${statement_type || 'P&L'} — ${new Date().toLocaleDateString()}`;

      // Source-file metadata (filenames, boundaries) — lets the user re-upload later
      // to unlock cell-reference Excel output. NEVER stores raw bytes.
      const processingConfig = {
        source_file_name: fileDataSnapshot?.file?.name || null,
        source_sheet_name: resultsObj.sheet_name || null,
        header_end_row: resultsObj.header_end_row ?? null,
        data_start_row: resultsObj.data_start_row ?? null,
        period_options: resultsObj.period_options || null,
      };

      // BLI-8: persist NPC payload + approval state so saved mapper sessions
      // restore the breakdown panel + balancing banner.
      const npcPayload = netProfitCheck || null;
      const npcApprovals = (approvedSuggestions.size || rejectedSuggestions.size)
        ? { approved: [...approvedSuggestions], rejected: [...rejectedSuggestions] }
        : null;

      const res = await saveSession({
        name,
        feature_key: 'mapping',
        entity_count: 0,
        account_count: (accounts || []).length,
        period_count: totalPeriods || 0,
        comparable_count: 0,
        // Only persistable fields — source_file_base64 is excluded (can be multi-MB)
        results_data: {
          accounts, accountsByPeriod, totalPeriods, statement_type, company_context, period_identifier,
          net_profit_check: npcPayload,
          npc_approvals: npcApprovals,
        },
        processing_config: processingConfig,
      });

      const newId = res?.session_id || null;
      setCurrentMapperSessionId(newId);
      fetchMapperSessions();
      return newId;
    } catch (err) {
      logger.error('Failed to auto-save mapper session:', err);
      return null;
    }
  }, [fetchMapperSessions, netProfitCheck, approvedSuggestions, rejectedSuggestions]);

  const handlePinMapperSession = useCallback(async (sessionId, qoeReport = null) => {
    if (!sessionId) return;
    try {
      await pinSession(sessionId, true, qoeReport);
      fetchMapperSessions();
      setToast({ message: qoeReport ? 'Saved with QoE report' : 'Session saved', visible: true });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      logger.error('Failed to pin mapper session:', err);
    }
  }, [fetchMapperSessions]);

  const handleUpdateMapperSessionResults = useCallback(async (sessionId, payload) => {
    if (!sessionId || !payload) return;
    try {
      await updateSessionResults(sessionId, payload);
      fetchMapperSessions();
    } catch (err) {
      logger.error('Failed to persist mapper session edits:', err);
    }
  }, [fetchMapperSessions]);

  const handleAttachQoeToMapperSession = useCallback(async (sessionId, qoeReport) => {
    if (!sessionId || !qoeReport) return;
    try {
      await attachQoeReport(sessionId, qoeReport);
      fetchMapperSessions();
    } catch (err) {
      logger.error('Failed to attach QoE report to session:', err);
    }
  }, [fetchMapperSessions]);

  const handleUnpinMapperSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      await unpinSession(sessionId);
      fetchMapperSessions();
    } catch (err) {
      logger.error('Failed to unpin mapper session:', err);
    }
  }, [fetchMapperSessions]);

  const handleLoadSession = useCallback(async (sessionId, featureKey = 'comparative') => {
    try {
      setLoadedSessionId(sessionId);
      setError(null);
      setSessionLoading(true);

      if (featureKey === 'mapping') {
        setActivePage('mapper');
        setStep(3);
        setDataFormat('pl');
        setIsViewingSavedMapperSession(true);
        setResults(null);
        setProgress(null);
        // Clear the inactive workflow so a stale comparative run isn't lurking.
        setComparativeResults(null);
        setComparativeProgress(null);
        setComparativeData(null);
        setComparativeEbitdaChecks({});
        setComparativeStep(1);

        const data = await getSession(sessionId);
        // Merge processing_config back into results so generateExcelTemplate
        // can read sheet_name / header_end_row / etc from the same keys it always has.
        const cfg = data.processing_config || {};
        setResults({
          ...data.results_data,
          sheet_name: cfg.source_sheet_name || data.results_data?.sheet_name || null,
          header_end_row: cfg.header_end_row ?? data.results_data?.header_end_row ?? null,
          data_start_row: cfg.data_start_row ?? data.results_data?.data_start_row ?? null,
          period_options: cfg.period_options || data.results_data?.period_options || null,
          source_file_base64: null,
          source_file_name: cfg.source_file_name || null,
        });
        setStatementType(data.results_data?.statement_type || null);
        setProgress(null);
        setCurrentMapperSessionId(sessionId);
        setMapperSourceFileName(cfg.source_file_name || null);
        setMapperQoeResults(data.qoe_report || null);
        setMapperQoeShowPanel(!!data.qoe_report);
        setMapperProcessedAt(data.created_at || null);

        // BLI-8: rehydrate the NPC payload + approval state. Old sessions
        // saved before this change will have neither field — gracefully
        // leave the panel hidden in that case.
        const savedNpc = data.results_data?.net_profit_check || null;
        const savedApprovals = data.results_data?.npc_approvals || null;
        setNetProfitCheck(savedNpc);
        setApprovedSuggestions(new Set(savedApprovals?.approved || []));
        setRejectedSuggestions(new Set(savedApprovals?.rejected || []));
      } else {
        setActivePage('comparative');
        setComparativeStep(3);
        setComparativeResults(null);
        setComparativeProgress(null);
        // Clear the inactive workflow so a stale mapper run isn't lurking.
        setResults(null);
        setProgress(null);
        setStatementType(null);
        setPeriodOptions(null);
        setFileData(null);
        setDataFormat(null);
        setStep(1);
        setIsViewingSavedMapperSession(false);
        setCurrentMapperSessionId(null);
        setMapperProcessedAt(null);
        setMapperSourceFileName(null);
        setMapperQoeResults(null);
        setMapperQoeShowPanel(false);

        const data = await getSession(sessionId);
        setComparativeResults({
          ...data.results_data,
          source_file_base64: null,
          comparable_metadata: null,
        });
        setComparativeProgress(null);

        // BLI-8: rehydrate NPC + approvals for comparative too (currently
        // NPC doesn't run in this flow, but persistence is symmetric and
        // future-proofs any move). Old sessions: both fields will be null.
        const savedNpc = data.results_data?.net_profit_check || null;
        const savedApprovals = data.results_data?.npc_approvals || null;
        setNetProfitCheck(savedNpc);
        setApprovedSuggestions(new Set(savedApprovals?.approved || []));
        setRejectedSuggestions(new Set(savedApprovals?.rejected || []));
      }

      setSessionLoading(false);
      setToast({ message: 'Session loaded successfully', visible: true });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      logger.error('Failed to load session:', err);
      setError('Failed to load saved session');
      setSessionLoading(false);
    }
  }, []);

  const handleDeleteSession = useCallback(async (sessionId, featureKey = 'comparative') => {
    try {
      await deleteSession(sessionId);
      if (loadedSessionId === sessionId) {
        setLoadedSessionId(null);
      }
      if (currentMapperSessionId === sessionId) {
        setCurrentMapperSessionId(null);
      }
      if (featureKey === 'mapping') {
        fetchMapperSessions();
      } else {
        fetchSessions();
      }
    } catch (err) {
      logger.error('Failed to delete session:', err);
    }
  }, [loadedSessionId, currentMapperSessionId, fetchSessions, fetchMapperSessions]);

  // Sidebar tool clicks route through here so switching tools always lands on
  // the destination's start screen with no stale state from a prior session
  // or in-progress flow. See plan: stateful-weaving-ladybug.
  const handleSelectPage = useCallback((page) => {
    setLoadedSessionId(null);
    setSessionLoading(false);

    setCurrentMapperSessionId(null);
    setIsViewingSavedMapperSession(false);
    setMapperProcessedAt(null);
    setMapperSourceFileName(null);
    setMapperQoeResults(null);
    setMapperQoeShowPanel(false);
    setResults(null);
    setProgress(null);
    setNetProfitCheck(null);
    setApprovedSuggestions(new Set());
    setRejectedSuggestions(new Set());
    setStatementType(null);
    setPeriodOptions(null);
    setFileData(null);
    setDataFormat(null);
    setStep(1);

    setComparativeResults(null);
    setComparativeProgress(null);
    setComparativeData(null);
    setComparativeEbitdaChecks({});
    setComparativeStep(1);

    // Clicking the Agent sidebar button (or any other top-level tool)
    // should always restart the agent flow — mirroring what the in-page
    // "Map Another" button does. Without this, navigating from a resumed
    // session back to the Agent tool would keep the session ID set, the
    // URL effect would route back to #/agent/session/<id>, and the Agent
    // component would remount with initialSessionId still pointing at the
    // previous run.
    setCurrentAgentSessionId(null);
    setCurrentBalanceSheetSessionId(null);
    setCurrentTBToBSSessionId(null);
    setCurrentUnifiedSessionId(null);
    setCurrentDatabookSessionId(null);

    setError(null);
    setLoading(false);

    setActivePage(page);
  }, []);

  // Open the account page WITHOUT resetting tool state. Stores the current
  // page so the back button can return the user to where they were.
  const handleOpenAccount = useCallback(() => {
    setActivePage((current) => {
      if (current && current !== 'account') {
        setPreviousPage(current);
      }
      return 'account';
    });
  }, []);

  // Close the account page and return to wherever the user came from.
  // Falls back to mapper when previous page is unknown (e.g. hard refresh on /account).
  const handleCloseAccount = useCallback(() => {
    setActivePage(previousPage || 'mapper');
  }, [previousPage]);

  // Check authentication on app load — checks Supabase localStorage AND the
  // backend HttpOnly cookie in parallel. Either being valid means the user
  // is authenticated. This fixes BLI-10: the old version only consulted
  // Supabase, so a refresh that found Supabase's cache empty would kick the
  // user to login even when the backend cookie + Redis session were alive.
  useEffect(() => {
    // Check URL for password recovery or invite flow FIRST (before Supabase processes)
    const hash = window.location.hash;
    const path = window.location.pathname;

    // Check if URL indicates password recovery
    // Supabase sends: /reset-password#access_token=...&type=recovery
    // After Supabase processes, the hash may be empty but path remains
    const isRecoveryUrl = path.includes('reset-password') || hash.includes('type=recovery');

    // Check if URL indicates invite flow
    // Supabase sends: #access_token=...&type=invite or type=signup
    const isInviteUrl = hash.includes('type=invite') || hash.includes('type=signup');

    if (isRecoveryUrl || isInviteUrl) {
      recoveryModeRef.current = true;
      setPasswordRecoveryMode(true);
    }

    // Subscribe FIRST so we don't miss PASSWORD_RECOVERY / TOKEN_REFRESHED.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, supaSession) => {
        if (event === 'PASSWORD_RECOVERY') {
          recoveryModeRef.current = true;
          setPasswordRecoveryMode(true);
          setSession(supaSession);
          setIsAuthenticated(false);
          setAuthLoading(false);
          window.history.replaceState(null, '', '/');
          return;
        }

        if (recoveryModeRef.current) {
          setSession(supaSession);
          return;
        }

        // Silent JWT rotation by Supabase. The cookie still holds the old
        // JWT; re-establish it so backend calls continue to authenticate.
        if (event === 'TOKEN_REFRESHED' && supaSession?.access_token) {
          logger.info('Supabase token refreshed — re-establishing backend cookie');
        }

        setSession(supaSession);
        if (supaSession?.user) {
          await setSessionCookie(supaSession.access_token);
          setIsAuthenticated(true);
          setCurrentUser(supaSession.user.email);
        } else if (event === 'SIGNED_OUT') {
          // Only flip to unauthenticated on an explicit sign-out. Transient
          // null sessions during init are owned by the mount probe below.
          setIsAuthenticated(false);
          setCurrentUser(null);
          lastCookieTokenRef.current = null;
        }
      }
    );

    // Cold-start probe: Supabase localStorage AND backend cookie in parallel.
    // Whichever returns valid auth wins; both invalid → show login.
    Promise.all([
      supabase.auth.getSession(),
      getSessionStatus(),
    ]).then(async ([supaResult, backendStatus]) => {
      const supaSession = supaResult?.data?.session;

      if (recoveryModeRef.current) {
        setSession(supaSession);
        setIsAuthenticated(false);
        setAuthLoading(false);
        window.history.replaceState(null, '', '/');
        return;
      }

      setSession(supaSession);

      if (supaSession?.user) {
        // Supabase rehydrated — refresh the backend cookie with the (possibly
        // newer) JWT and flip to authenticated.
        await setSessionCookie(supaSession.access_token);
        setIsAuthenticated(true);
        setCurrentUser(supaSession.user.email);
      } else if (backendStatus?.active) {
        // Supabase localStorage came back empty but the backend cookie is
        // still alive. Trust it. The next backend call's response (or the
        // next Supabase auto-refresh) will reconcile the two.
        setIsAuthenticated(true);
        setCurrentUser(backendStatus.user_email || null);
      }
      // Both empty → isAuthenticated stays false → login screen.

      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // URL ←→ state sync (BLI-10).
  // Hash format:
  //   #/<page>                       e.g. #/mapper, #/comparative
  //   #/<page>/session/<sessionId>   e.g. #/mapper/session/abc123
  //
  // We use the hash (not pathname) because it's already where the recovery /
  // invite flows live, no Vercel rewrite changes are needed, and there's no
  // router library in this app.

  // Restore session from URL once auth has resolved. Runs at most once per
  // mount; subsequent navigation is owned by handleSelectPage / handleLoadSession.
  const urlRestoredRef = useRef(false);
  useEffect(() => {
    if (authLoading || !isAuthenticated || urlRestoredRef.current) return;

    const h = window.location.hash;

    // Skip Supabase auth-flow hashes (handled by the auth effect).
    if (!h.startsWith('#/') || h.includes('access_token=') || h.includes('type=')) {
      urlRestoredRef.current = true;
      return;
    }

    const m = h.match(/^#\/([a-z-]+)\/session\/([A-Za-z0-9-]+)/);
    if (m) {
      const page = m[1];
      const sessionId = m[2];
      if (page === 'agent') {
        // Agent sessions live in a different store with their own /agent
        // endpoints; the Agent component handles fetching+reconstructing.
        // All App.tsx has to do is route to the page and pass the id down.
        setActivePage('agent');
        setCurrentAgentSessionId(sessionId);
      } else if (page === 'balance-sheet') {
        // Balance-sheet sessions are agent sessions too (same /agent store,
        // tagged statement_type='bs'); route to the isolated BS page.
        setActivePage('balance-sheet');
        setCurrentBalanceSheetSessionId(sessionId);
      } else if (page === 'tb-to-bs') {
        // TB→BS sessions are agent sessions too (tagged statement_type='tb_bs');
        // route to the isolated TB→BS page.
        setActivePage('tb-to-bs');
        setCurrentTBToBSSessionId(sessionId);
      } else if (page === 'unified') {
        // Unified parents are agent sessions (session_kind='unified'); the
        // UnifiedAgent component loads the parent snapshot + children itself.
        setActivePage('unified');
        setCurrentUnifiedSessionId(sessionId);
      } else if (page === 'databook') {
        // Databook parents are agent sessions (session_kind='databook'); the
        // DatabookAgent component loads the parent snapshot + children itself.
        setActivePage('databook');
        setCurrentDatabookSessionId(sessionId);
      } else {
        const featureKey = page === 'mapper' ? 'mapping' : 'comparative';
        handleLoadSession(sessionId, featureKey);
      }
    }
    urlRestoredRef.current = true;
  }, [authLoading, isAuthenticated, handleLoadSession]);

  // Push state to the URL hash whenever the active page or loaded session id
  // changes. replaceState (not pushState) so the browser back button still
  // exits the app instead of stepping through every internal nav.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !activePage) return;

    const currentHash = window.location.hash;
    // Don't clobber Supabase auth-flow hashes mid-flow.
    if (currentHash.includes('access_token=') || currentHash.includes('type=')) return;

    let next;
    if (activePage === 'mapper' && currentMapperSessionId) {
      next = `#/mapper/session/${currentMapperSessionId}`;
    } else if (activePage === 'comparative' && loadedSessionId) {
      next = `#/comparative/session/${loadedSessionId}`;
    } else if (activePage === 'agent' && currentAgentSessionId) {
      next = `#/agent/session/${currentAgentSessionId}`;
    } else if (activePage === 'balance-sheet' && currentBalanceSheetSessionId) {
      next = `#/balance-sheet/session/${currentBalanceSheetSessionId}`;
    } else if (activePage === 'tb-to-bs' && currentTBToBSSessionId) {
      next = `#/tb-to-bs/session/${currentTBToBSSessionId}`;
    } else if (activePage === 'unified' && currentUnifiedSessionId) {
      next = `#/unified/session/${currentUnifiedSessionId}`;
    } else if (activePage === 'databook' && currentDatabookSessionId) {
      next = `#/databook/session/${currentDatabookSessionId}`;
    } else {
      next = `#/${activePage}`;
    }

    if (currentHash !== next) {
      window.history.replaceState(null, '', next);
    }
  }, [activePage, currentMapperSessionId, loadedSessionId, currentAgentSessionId, currentBalanceSheetSessionId, currentTBToBSSessionId, currentUnifiedSessionId, currentDatabookSessionId, authLoading, isAuthenticated]);

  // Fetch saved sessions when user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      fetchSessions();
      fetchMapperSessions();
      fetchAgentSessions();
    }
  }, [isAuthenticated, fetchSessions, fetchMapperSessions, fetchAgentSessions]);

  // Listen for session-expired events from fetch/axios interceptors
  useEffect(() => {
    const handleSessionExpired = () => {
      // Only trigger if user is currently authenticated (avoid spurious 401s during login)
      if (isAuthenticated && !sessionExpired) {
        logger.warn('Session expired — showing re-auth modal');
        setSessionExpired(true);
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [isAuthenticated, sessionExpired]);

  // Re-authentication handler — called from SessionExpiredModal after successful login
  const handleReauthenticate = async (user, session) => {
    // Re-establish the HttpOnly session cookie
    lastCookieTokenRef.current = null; // Force fresh cookie
    await setSessionCookie(session.access_token);
    setSession(session);
    setCurrentUser(user.email);
    // Safety net: clear residual global error/loading from the request that triggered
    // the modal in case a catch block forgot its SessionExpiredError guard.
    // Step/fileData/periodOptions/results are intentionally preserved — they represent
    // pre-button-press progress.
    setError(null);
    setLoading(false);
    // Dismiss the modal — all app state is preserved
    setSessionExpired(false);
  };

  const handleLogin = async (user, session) => {
    // Set HttpOnly cookie on backend (uses helper to prevent duplicates)
    await setSessionCookie(session.access_token);
    setSession(session);
    setIsAuthenticated(true);
    setCurrentUser(user.email);
  };

  const handleLogout = async () => {
    // Revoke session server-side first (invalidates token in Redis)
    try {
      await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
      });
    } catch (err) {
      logger.error('Server logout error:', err);
    }
    
    // Then sign out from Supabase
    try {
      await supabase.auth.signOut();
    } catch (err) {
      logger.error('Supabase logout error:', err);
    }
    
    // Clear cookie token ref so next login sets a fresh cookie
    lastCookieTokenRef.current = null;
    
    setSession(null);
    setIsAuthenticated(false);
    setCurrentUser(null);
    setTenantInfo(null); // Clear tenant info on logout
    // Reset app state
    setStep(1);
    setFileData(null);
    setDataFormat(null);
    setStatementType(null);
    setPeriodOptions(null); 
    setResults(null);
    setError(null);
    setLoading(false);
    setProgress(null);
    setNetProfitCheck(null);
    setApprovedSuggestions(new Set());
    setRejectedSuggestions(new Set());
    // Reset comparative state
    setComparativeStep(1);
    setComparativeData(null);
    setComparativeResults(null);
    setComparativeProgress(null);
    setComparativeEbitdaChecks({});
    setLoadedSessionId(null);
    setSessionLoading(false);
    setToast(null);
    setSavedSessions([]);
    setActivePage('mapper');
    // Clear deep-link hash so the URL doesn't show #/mapper/session/<id>
    // after logout. URL-restore is one-shot per mount, so this is mainly
    // cosmetic.
    if (window.location.hash.startsWith('#/')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  // Handler for when tenant is set up or updated via AccountOverview
  // Memoized to prevent infinite re-render loop in AccountOverview's useEffect
  const handleTenantSetup = useCallback((result) => {
    setTenantInfo({
      name: result.tenant_name,
      slug: result.tenant_slug,
      id: result.tenant_id
    });
  }, []);

  // Show loading spinner while checking auth
  if (authLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login screen if not authenticated (or in password recovery mode)
  if (!isAuthenticated || passwordRecoveryMode) {
    return (
      <Login 
        onLogin={handleLogin} 
        passwordRecoveryMode={passwordRecoveryMode}
        onPasswordResetComplete={() => {
          recoveryModeRef.current = false;
          setPasswordRecoveryMode(false);
          setIsAuthenticated(false);
        }}
      />
    );
  }

  const handleFileUpload = (file, sheetName, statementType, dataFormat, selectedConfig = null) => {
    setFileData({ file, sheetName, selectedConfig });
    setStatementType(statementType);
    setDataFormat(dataFormat);
    setStep(2);
    setError(null);
    // Clear stale results from previous file so user can't navigate to outdated results
    setResults(null);
    setProgress(null);
    setNetProfitCheck(null);
    setApprovedSuggestions(new Set());
    setRejectedSuggestions(new Set());
    setMapperQoeResults(null);
    setMapperQoeShowPanel(false);
    setIsViewingSavedMapperSession(false);
    setCurrentMapperSessionId(null);
    setMapperProcessedAt(null);
    setMapperSourceFileName(null);
  };

  const handlePeriodDiscovery = (options, dataFormat) => {
    setPeriodOptions(options);
    setDataFormat(dataFormat); // Store dataFormat from discovery
    setLoading(false);
    setError(null);
  };

  // Comparative Processing Handler - SEQUENTIAL RAW DATA + PARALLEL COMPARABLES
  // 1. First: Process Raw Data (ERP format) and wait for completion
  // 2. Then: Process all Comparable sheets in parallel
  const handleComparativeProcess = async (commonPeriods) => {
    logger.log('🚀 Starting comparative processing for periods:', commonPeriods);
    
    if (!periodOptions || !periodOptions.rawData || !periodOptions.comparables) {
      logger.error('Missing comparative data in periodOptions');
      setError('Missing comparative data. Please restart the analysis.');
      return;
    }
    
    handleProcessStart();
    setStep(3);
    
    const file = periodOptions.file || fileData.file;
    const companyContext = periodOptions.rawData.company_context || '';
    const comparables = periodOptions.comparables;
    
    // Initialize sections for progress tracking
    const sections = [
      { id: 'raw_data', name: `Raw Data: ${periodOptions.rawSheetName}`, processed: 0, total: 0, status: 'active' },
      ...comparables.map((comp, i) => ({
        id: `comp_${i}`,
        name: `Comparable: ${comp.sheet}`,
        processed: 0,
        total: 0,
        status: 'pending'
      }))
    ];
    
    // Initialize progress
    setProgress({
      processedPeriods: 0,
      totalPeriods: commonPeriods.length,
      currentPeriod: 'Processing Raw Data...',
      completed: false,
      mappedAccounts: 0,
      totalAccounts: 0,
      mappingProgress: 0,
      sections: sections,
      completedStreams: 0,
      totalStreams: 1 + comparables.length
    });
    
    // Convert source file to base64 for referencing mode
    let sourceFileBase64 = null;
    try {
      const fileArrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(fileArrayBuffer);
      let binary = '';
      bytes.forEach(byte => binary += String.fromCharCode(byte));
      sourceFileBase64 = btoa(binary);
    } catch (e) {
      console.warn('Could not convert source file to base64:', e);
    }
    
    // Build comparable metadata for referencing (period column maps + header info)
    const comparableMetadata = {};
    const filterPeriodsForMeta = (periodOpts, sheetName) => {
      return (periodOpts || []).filter(period => {
        const displayName = period.display_name || '';
        return commonPeriods.some(cp => {
          if (typeof cp === 'object') {
            const originalNames = cp.original_names || {};
            return originalNames[sheetName] === displayName || cp.common_name === displayName;
          }
          return displayName === String(cp);
        });
      });
    };
    comparables.forEach(comp => {
      const filteredPeriods = filterPeriodsForMeta(comp.data.period_options || [], comp.sheet);
      const periodColumnMap = {};
      filteredPeriods.forEach(p => {
        if (p.period_index !== undefined && p.column_index !== undefined) {
          periodColumnMap[p.period_index] = p.column_index;
        }
      });
      comparableMetadata[comp.sheet] = {
        header_end_row: comp.data.header_end_row || 0,
        data_start_row: comp.data.data_start_row ?? null,
        period_column_map: periodColumnMap
      };
    });
    
    setResults({
      period_identifier: `${commonPeriods.length} Common Periods`,
      accounts: [],
      accountsByEntity: {},
      totalPeriods: commonPeriods.length,
      statement_type: 'comparative_income_statement',
      company_context: companyContext,
      // Store for referencing feature
      source_file_base64: sourceFileBase64,
      raw_data_sheet_name: periodOptions.rawSheetName,
      comparable_metadata: comparableMetadata
    });
    
    logger.log('📤 COMPARATIVE PROCESSING ORDER:');
    logger.log('  1️⃣ Raw sheet (first):', periodOptions.rawSheetName);
    logger.log('  2️⃣ Comparables (parallel after raw):', comparables.map(c => c.sheet));
    logger.log('  📊 Common periods:', commonPeriods.length);
    
    // Track completed streams
    let completedStreams = 0;
    const totalStreams = 1 + comparables.length;
    
    // Helper to process a single SSE stream
    const processStream = async (response, sectionId, sourceType, sheetName) => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim() === '') continue;
            
            try {
              const parsedData = JSON.parse(data);
              
              if (parsedData.status === 'complete' || parsedData.status === 'starting_raw_data' || parsedData.status === 'starting_optimized') {
                // Stream completed or started - update section status
                if (parsedData.status === 'complete') {
                  setProgress(prev => ({
                    ...prev,
                    sections: prev.sections.map(s => 
                      s.id === sectionId 
                        // Don't overwrite processed with total_accounts - it's total entries (accounts × periods), not unique accounts
                        // Keep s.processed from mapping_progress, or use s.total to show 100% complete
                        ? { ...s, status: 'completed', processed: s.total || s.processed }
                        : s
                    )
                  }));
                }
              } else if (parsedData.status === 'mapping_progress') {
                // Update section-specific progress
                setProgress(prev => ({
                  ...prev,
                  currentPeriod: `[${sheetName}] Mapping: ${parsedData.account_name} → ${parsedData.category}`,
                  sections: prev.sections.map(s => 
                    s.id === sectionId 
                      ? { ...s, processed: parsedData.completed, total: parsedData.total }
                      : s
                  )
                }));
              } else if (parsedData.error) {
                logger.error(`Error in stream ${sectionId}:`, parsedData.error);
                throw new Error(parsedData.error);
              } else if (parsedData.original_name) {
                // Account result - add to results with source metadata
                const accountWithMeta = {
                  ...parsedData,
                  source_type: parsedData.source_type || sourceType,
                  source_sheet: parsedData.source_sheet || sheetName,
                  entity: parsedData.entity || sheetName
                };
                
                setResults(prev => {
                  const accounts = [...(prev.accounts || []), accountWithMeta];
                  
                  // Group by entity for comparative display
                  const accountsByEntity = { ...(prev.accountsByEntity || {}) };
                  const entity = accountWithMeta.entity || 'Unknown';
                  if (!accountsByEntity[entity]) {
                    accountsByEntity[entity] = [];
                  }
                  accountsByEntity[entity].push(accountWithMeta);
                  
                  return {
                    ...prev,
                    accounts,
                    accountsByEntity
                  };
                });
              }
            } catch (parseError) {
              logger.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
      
      // Stream finished
      completedStreams++;
      logger.log(`✅ Stream ${sectionId} completed (${completedStreams}/${totalStreams})`);
      
      setProgress(prev => ({
        ...prev,
        completedStreams: completedStreams
      }));
    };
    
    // Filter period_options to only include common periods (for comparable sheets)
    const filterPeriodsToCommon = (periodOptions, sheetName) => {
      return periodOptions.filter(period => {
        const displayName = period.display_name || '';
        return commonPeriods.some(cp => {
          if (typeof cp === 'object') {
            const originalNames = cp.original_names || {};
            return originalNames[sheetName] === displayName || cp.common_name === displayName;
          }
          return displayName === String(cp);
        });
      });
    };
    
    try {
      // === STEP 1: PROCESS RAW DATA FIRST ===
      logger.log('1️⃣ Starting Raw Data processing...');
      
      const rawFormData = new FormData();
      rawFormData.append('file', file);
      rawFormData.append('sheet_name', periodOptions.rawSheetName);
      rawFormData.append('entity_column', 'Entity');
      rawFormData.append('account_column', 'Account');
      rawFormData.append('date_column', 'Date');
      rawFormData.append('value_column', 'Value');
      rawFormData.append('common_periods', JSON.stringify(commonPeriods));
      rawFormData.append('company_context', companyContext);
      
      const rawResponse = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/stream-process-raw-data/`, {
        method: 'POST',
        body: rawFormData,
        credentials: 'include',
        headers: {
          'Accept': 'text/event-stream',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
      });
      
      if (!rawResponse.ok) {
        const errorText = await rawResponse.text();
        throw new Error(`Raw data request failed: ${rawResponse.status} - ${errorText}`);
      }
      
      // Process raw data stream and wait for completion
      await processStream(rawResponse, 'raw_data', 'entity', periodOptions.rawSheetName);
      logger.log('✅ Raw Data processing complete!');
      
      // === STEP 2: PROCESS COMPARABLES IN PARALLEL ===
      if (comparables.length > 0) {
        logger.log(`2️⃣ Starting ${comparables.length} Comparable sheets in parallel...`);
        
        // Update progress to show comparables starting
        setProgress(prev => ({
          ...prev,
          currentPeriod: 'Processing Comparable sheets...',
          sections: prev.sections.map(s => 
            s.id.startsWith('comp_') ? { ...s, status: 'active' } : s
          )
        }));
        
        const comparablePromises = comparables.map(async (comp, idx) => {
          const compFormData = new FormData();
          compFormData.append('file', file);
          compFormData.append('sheet_name', comp.sheet);
          
          // Filter periods to only common ones
          const filteredPeriods = filterPeriodsToCommon(comp.data.period_options || [], comp.sheet);
          compFormData.append('period_options', JSON.stringify(filteredPeriods));
          
          compFormData.append('account_name_column', String(comp.data.account_name_column || 0));
          compFormData.append('header_start_row', String(comp.data.header_start_row || 0));
          compFormData.append('header_end_row', String(comp.data.header_end_row || 0));
          compFormData.append('company_context', companyContext);
          compFormData.append('statement_type', 'comparative_income_statement');
          
          const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/stream-classify-accounts/`, {
            method: 'POST',
            body: compFormData,
            credentials: 'include',
            headers: {
              'Accept': 'text/event-stream',
              'x-api-key': import.meta.env.VITE_API_KEY,
            },
          });
          
          if (!response.ok) {
            throw new Error(`Comparable ${comp.sheet} request failed: ${response.status}`);
          }
          
          return processStream(response, `comp_${idx}`, 'comparable', comp.sheet);
        });
        
        // Wait for all comparables to complete
        await Promise.all(comparablePromises);
        logger.log('✅ All Comparable sheets processed!');
      }
      
      // === ALL PROCESSING COMPLETE ===
      logger.log('🎉 All comparative processing complete!');
      setLoading(false);
      setProgress(prev => ({
        ...prev,
        completed: true,
        currentPeriod: 'All processing complete!',
        completedStreams: totalStreams
      }));

      // Auto-save session (fire-and-forget, read from ref for latest state)
      saveComparativeSession(resultsRef.current).catch(e => logger.error('Auto-save failed:', e));

    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('Comparative processing error:', error);
      setError(error.message || 'Comparative processing failed');
      setLoading(false);
    }
  };

  // Multi-period streaming handler
  const handleMultiPeriodStreamProcess = async (selectedPeriods, useDeterministic, maxRows, options = {}) => {
    handleProcessStart();
    setStep(3);
    // Reset and initialize progress for new processing
    setProgress({ 
      processedPeriods: 0, 
      totalPeriods: selectedPeriods.length, 
      currentPeriod: null,
      completed: false
    });
    // Store period metadata and source file info for referencing feature
    // Convert source file to base64 for potential referencing mode
    let sourceFileBase64 = null;
    try {
      const fileArrayBuffer = await fileData.file.arrayBuffer();
      const bytes = new Uint8Array(fileArrayBuffer);
      let binary = '';
      bytes.forEach(byte => binary += String.fromCharCode(byte));
      sourceFileBase64 = btoa(binary);
    } catch (e) {
      console.warn('Could not convert source file to base64:', e);
    }
    
    setResults({ 
      period_identifier: `${selectedPeriods.length} Periods`, 
      accounts: [],
      accountsByPeriod: {},
      totalPeriods: selectedPeriods.length,
      // Store for referencing feature
      period_options: selectedPeriods,
      header_end_row: periodOptions.header_end_row,
      data_start_row: periodOptions.data_start_row,
      sheet_name: fileData.sheetName,
      source_file_base64: sourceFileBase64
    });
    
    const formData = new FormData();
    formData.append('file', fileData.file);
    formData.append('sheet_name', fileData.sheetName);
    formData.append('period_options', JSON.stringify(selectedPeriods));
    formData.append('account_name_column', periodOptions.account_name_column.toString());
    formData.append('header_start_row', periodOptions.header_start_row.toString());
    formData.append('header_end_row', periodOptions.header_end_row.toString());
    // Pass data boundaries to exclude non-financial rows (memo/audit/tronc)
    if (periodOptions.data_start_row != null) {
      formData.append('data_start_row', periodOptions.data_start_row.toString());
    }
    if (periodOptions.data_end_row != null) {
      formData.append('data_end_row', periodOptions.data_end_row.toString());
    }
    // Pass the auto-detected company context from discover-periods
    const companyContext = periodOptions.company_context || '';
    formData.append('company_context', companyContext);
    // Pass the statement type selected by the user
    formData.append('statement_type', statementType || 'income_statement');
    // Pass config_id if a custom configuration was selected (specific config ID, not just tenant_id)
    // This ensures the backend uses the exact config the user selected, not just any config for the tenant
    const configId = fileData.selectedConfig?.id || '';
    if (configId) {
      formData.append('config_id', configId);
    }
    
    if (typeof maxRows === 'number' && !isNaN(maxRows)) {
      formData.append('max_rows', maxRows.toString());
    }
    // Important: pass deterministic toggle to backend
    formData.append('use_deterministic_mapping', String(useDeterministic ?? true));

    // Decide endpoint based on admin/forceValidation
    const endpointPath = '/stream-classify-accounts/';

    try {
      const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}${endpointPath}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Accept': 'text/event-stream',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
        throw new Error(errorData.detail || 'Server error occurred');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = ''; // Buffer for incomplete SSE messages
      let netProfitCheckActive = false; // Track net profit check phase locally
      // BLI-8: capture the auto-save's session-id promise so the NPC-complete
      // handler can PATCH the saved session with the full NPC payload once
      // the (delayed) net_profit_check event arrives.
      let mapperSavePromise = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Append new chunk to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Split by newlines, but keep last incomplete line in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Last element might be incomplete

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim() === '') continue;

            try {
              const parsedData = JSON.parse(data);
              
              if (parsedData.status === 'complete' || parsedData.status === 'complete_with_warnings' || parsedData.status === 'processing_complete') {
                setLoading(false);
                // Only mark as completed if no net profit check is pending —
                // the net_profit_check handler will set completed when it finishes.
                if (!netProfitCheckActive) {
                  setProgress(prev => ({
                    ...prev,
                    processedPeriods: prev?.totalPeriods || 0,
                    completed: true,
                    currentPeriod: 'Processing Complete'
                  }));
                }
                // Auto-save this mapper run into the rolling buffer (fire-and-forget).
                // Read from ref so we have the final accumulated accounts, not a stale closure.
                setMapperProcessedAt(new Date().toISOString());
                mapperSavePromise = saveMapperSession(resultsRef.current, undefined, fileData)
                  .catch(e => { logger.error('Mapper auto-save failed:', e); return null; });
              } else if (parsedData.status === 'starting_optimized') {
                // Initial status for optimized processing
                setResults(prev => ({
                  ...prev,
                  statement_type: parsedData.statement_type || prev?.statement_type
                }));
                setProgress({
                  processedPeriods: 0,
                  totalPeriods: parsedData.total_periods,
                  currentPeriod: 'Extracting unique accounts for AI mapping...',
                  completed: false,
                  mappedAccounts: 0,
                  totalAccounts: 0, // Will be updated when first mapping_progress arrives
                  mappingProgress: 0
                });
              } else if (parsedData.status === 'mapping_progress') {
                // Update progress for individual account mapping
                setProgress(prev => ({
                  ...prev,
                  currentPeriod: `Mapping: ${parsedData.account_name} → ${parsedData.category}`,
                  mappedAccounts: parsedData.completed || parsedData.progress?.completed,
                  totalAccounts: parsedData.total || parsedData.progress?.total,
                  mappingProgress: parsedData.progress_percentage
                }));
              } else if (parsedData.status === 'mapping_complete') {
                // Mapping phase complete, moving to period application
                setProgress(prev => ({
                  ...prev,
                  currentPeriod: 'Applying mappings to all periods...',
                  mappingComplete: true,
                  mappedAccounts: parsedData.completed ?? prev.mappedAccounts ?? 0,
                  totalAccounts: parsedData.total ?? prev.totalAccounts ?? 0,
                  mappingProgress: 100
                }));
              } else if (parsedData.status === 'period_progress') {
                // Update progress for period processing
                setProgress(prev => ({
                  ...prev,
                  processedPeriods: parsedData.processed_periods,
                  currentPeriod: `Processing: ${parsedData.period}`,
                }));
              } else if (parsedData.status === 'progress') {
                // Legacy progress tracker for non-optimized endpoints
                setProgress({
                  processedPeriods: parsedData.processed_periods,
                  totalPeriods: parsedData.total_periods,
                  currentPeriod: parsedData.current_period,
                  completed: false
                });
              } else if (parsedData.status === 'net_profit_check_started') {
                // Net profit reconciliation phase starting — keep progress active
                netProfitCheckActive = true;
                setProgress(prev => ({
                  ...prev,
                  completed: false,
                  currentPeriod: 'Checking if net profit balances with new mappings...',
                  mappingProgress: 100,
                }));
                setNetProfitCheck({ phase: 'checking', suggestions: [], result: null });
              } else if (parsedData.status === 'net_profit_check') {
                // Net profit check completed with suggestions + breakdown
                netProfitCheckActive = false;
                const checkStatus = parsedData.check_status || 'UNKNOWN';
                const suggestions = parsedData.suggestions || [];
                const npcPayload = {
                  phase: 'done',
                  suggestions: suggestions,
                  periodBreakdowns: parsedData.period_breakdowns || [],
                  categoryToSection: parsedData.category_to_section || {},
                  analysisSummary: parsedData.analysis_summary || '',
                  balancingPlan: parsedData.balancing_plan || null,
                  result: {
                    status: checkStatus,
                    reconciliation_summary: parsedData.reconciliation_summary || '',
                  },
                };
                setNetProfitCheck(npcPayload);
                setProgress(prev => ({
                  ...prev,
                  completed: true,
                  currentPeriod: suggestions.length > 0
                    ? `Net profit check — ${suggestions.length} suggestion(s) for review`
                    : 'Net profit check complete — balances match!',
                }));
                // BLI-8: NPC arrived after the first auto-save fired. PATCH the
                // saved session with the full payload so reload restores the panel.
                if (mapperSavePromise) {
                  mapperSavePromise.then(sessionId => {
                    if (!sessionId) return;
                    const r = resultsRef.current || {};
                    return updateSessionResults(sessionId, {
                      results_data: {
                        accounts: r.accounts,
                        accountsByPeriod: r.accountsByPeriod,
                        totalPeriods: r.totalPeriods,
                        statement_type: r.statement_type,
                        company_context: r.company_context,
                        period_identifier: r.period_identifier,
                        net_profit_check: npcPayload,
                        npc_approvals: null, // fresh NPC — no user approvals yet
                      },
                      account_count: (r.accounts || []).length,
                      period_count: r.totalPeriods || 0,
                      entity_count: 0,
                      comparable_count: 0,
                    });
                  }).catch(e => logger.error('NPC payload PATCH failed:', e));
                }
              } else if (parsedData.error) {
                throw new Error(parsedData.error);
              } else if (parsedData.original_name) {
                // This is an account - add it to results
                setResults(prev => {
                  const newAccounts = [...prev.accounts, parsedData];
                  const periodKey = parsedData.period_display_name || parsedData.period;
                  
                  // Group accounts by period
                  const newAccountsByPeriod = { ...prev.accountsByPeriod };
                  if (!newAccountsByPeriod[periodKey]) {
                    newAccountsByPeriod[periodKey] = [];
                  }
                  newAccountsByPeriod[periodKey] = [...newAccountsByPeriod[periodKey], parsedData];
                  
                  return {
                    ...prev,
                    accounts: newAccounts,
                    accountsByPeriod: newAccountsByPeriod,
                    statement_type: prev?.statement_type
                  };
                });
              }
            } catch (e) {
              logger.error('Error parsing streaming data');
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      handleError(err.message);
      setProgress(prev => prev ? { ...prev, completed: true } : prev);
    } finally {
      // Ensure loading is always turned off
      setLoading(false);
      // Keep progress state if it exists (don't reset to null)
    }
  };

  const handleProcessStart = () => {
    setLoading(true);
    setError(null);
  };

  const handleError = (errorMessage) => {
    // Silently ignore session-expired errors — the re-auth modal handles recovery
    if (errorMessage === 'Session expired') return;
    // Normalize error: backend may return detail as an object (e.g. quota exceeded)
    // which would crash React if rendered directly in JSX
    const msg = typeof errorMessage === 'object' && errorMessage !== null
      ? (errorMessage.message || JSON.stringify(errorMessage))
      : errorMessage;
    setError(msg);
    setLoading(false);
    // Keep progress state if it exists (don't reset to null)
  };

  const handleReset = () => {
    setStep(1);
    setFileData(null);
    setDataFormat(null);
    setStatementType(null);
    setPeriodOptions(null);
    setResults(null);
    setError(null);
    setLoading(false);
    setProgress(null); // Reset progress when starting completely over
    setNetProfitCheck(null); // Reset net profit check state
    setApprovedSuggestions(new Set());
    setRejectedSuggestions(new Set());
    // Reset QoE state when starting over
    setMapperQoeResults(null);
    setMapperQoeShowPanel(false);
    // Exit saved-session view mode so stepper returns for a new upload
    setIsViewingSavedMapperSession(false);
    setCurrentMapperSessionId(null);
    setMapperProcessedAt(null);
    setMapperSourceFileName(null);
  };

  const handleBackToPeriods = () => {
    setStep(2);
    // Preserve results and progress so user can navigate back to step 3 via progress indicator
    setError(null);
    setLoading(false);
  };

  // ============================================================================
  // MERGER HANDLERS
  // ============================================================================
  
  const handleMergerDiscoveryComplete = (data) => {
    setMergerData(data);
    setMergerStep(2);
    setLoading(false);
    setError(null);
    // Clear stale results from previous discovery
    setMergerResults(null);
    setMergerProgress(null);
    setMergerQoeResults(null);
    setMergerQoeShowPanel(false);
  };

  const handleMergerProcess = async (selectedPeriodIndices, files, sheetSelections, statementType, selectedConfig) => {
    
    handleProcessStart();
    setMergerStep(3);
    
    // Initialize progress
    setMergerProgress({
      processedPeriods: 0,
      totalPeriods: selectedPeriodIndices.length,
      currentPeriod: null,
      completed: false,
      mappedAccounts: 0,
      totalAccounts: 0,
      mappingProgress: 0
    });
    
    setMergerResults({
      period_identifier: `${selectedPeriodIndices.length} Merged Periods`,
      accounts: [],
      accountsByPeriod: {},
      totalPeriods: selectedPeriodIndices.length,
      statement_type: statementType || 'income_statement',
      isMerger: true,
      selectedConfig: selectedConfig // Store for ResultsDisplayMulti
    });
    
    // Build FormData
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('sheet_selections', JSON.stringify(sheetSelections));
    formData.append('selected_periods', JSON.stringify(selectedPeriodIndices));
    formData.append('statement_type', statementType || 'income_statement');
    
    // Pass discovery data to avoid re-discovery (saves LLM call!)
    const discoveryJson = JSON.stringify(mergerData?.discoveryResult || {});
    formData.append('discovery_data', discoveryJson);
    
    // Pass config_id if a custom configuration was selected
    const configId = selectedConfig?.id || '';
    if (configId) {
      formData.append('config_id', configId);
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/merger/stream-process/`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Accept': 'text/event-stream',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
        throw new Error(errorData.detail || 'Server error occurred');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim() === '') continue;

            try {
              const parsedData = JSON.parse(data);
              
              if (parsedData.status === 'complete') {
                setLoading(false);
                setMergerProgress(prev => ({
                  ...prev,
                  processedPeriods: prev?.totalPeriods || 0,
                  completed: true,
                  currentPeriod: 'Processing Complete'
                }));
              } else if (parsedData.status === 'starting_merger' || parsedData.status === 'merging_data') {
                setMergerProgress(prev => ({
                  ...prev,
                  currentPeriod: parsedData.message
                }));
              } else if (parsedData.status === 'starting_optimized') {
                setMergerResults(prev => ({
                  ...prev,
                  statement_type: parsedData.statement_type || prev?.statement_type,
                  sheets_merged: parsedData.sheets_merged
                }));
                setMergerProgress({
                  processedPeriods: 0,
                  totalPeriods: parsedData.total_periods,
                  currentPeriod: 'Mapping merged accounts...',
                  completed: false,
                  mappedAccounts: 0,
                  totalAccounts: parsedData.total_accounts || 0,
                  mappingProgress: 0
                });
              } else if (parsedData.status === 'mapping_progress') {
                setMergerProgress(prev => ({
                  ...prev,
                  currentPeriod: `Mapping: ${parsedData.account_name} → ${parsedData.category}`,
                  mappedAccounts: parsedData.completed || parsedData.progress?.completed,
                  totalAccounts: parsedData.total || parsedData.progress?.total,
                  mappingProgress: parsedData.progress_percentage
                }));
              } else if (parsedData.error) {
                throw new Error(parsedData.error);
              } else if (parsedData.original_name) {
                // This is an account - add it to results
                setMergerResults(prev => {
                  const newAccounts = [...prev.accounts, parsedData];
                  const periodKey = parsedData.period_display_name || parsedData.period;
                  
                  // Group accounts by period
                  const newAccountsByPeriod = { ...prev.accountsByPeriod };
                  if (!newAccountsByPeriod[periodKey]) {
                    newAccountsByPeriod[periodKey] = [];
                  }
                  newAccountsByPeriod[periodKey] = [...newAccountsByPeriod[periodKey], parsedData];
                  
                  return {
                    ...prev,
                    accounts: newAccounts,
                    accountsByPeriod: newAccountsByPeriod
                  };
                });
              }
            } catch (e) {
              logger.error('Error parsing JSON:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      handleError(err.message);
      setMergerProgress(prev => prev ? { ...prev, completed: true } : prev);
    } finally {
      setLoading(false);
    }
  };

  const handleMergerReset = () => {
    setMergerStep(1);
    setMergerData(null);
    setMergerResults(null);
    setMergerProgress(null);
    setError(null);
    setLoading(false);
    // Reset QoE state when starting over
    setMergerQoeResults(null);
    setMergerQoeShowPanel(false);
  };

  const handleMergerBackToPeriods = () => {
    setMergerStep(2);
    // Preserve results and progress so user can navigate back to step 3 via progress indicator
    setError(null);
    setLoading(false);
  };

  // ============================================================================
  // TB CONVERTER HANDLERS
  // ============================================================================
  
  const handleTBAnalysisComplete = (data) => {
    setTbConverterData(data);
    setTbConverterStep(2);
    setLoading(false);
    setError(null);
    // Clear stale results from previous analysis
    setTbConverterResults(null);
    setTbConverterProgress(null);
    setTbClassificationResult(null);
  };

  const handleTBPeriodReviewComplete = (data) => {
    // Move to classification step with updated period labels
    setTbConverterData(data);
    setTbConverterStep(3);
    setLoading(false);
    setError(null);
  };

  const handleTBClassificationComplete = (data) => {
    // Store classification result separately for persistence when navigating back
    setTbClassificationResult(data.classificationResult);
    setTbConverterData(data);
    handleTBConvert(data);
  };

  const handleTBConvert = async (conversionData) => {
    handleProcessStart();
    setTbConverterStep(4);
    
    // Initialize progress
    setTbConverterProgress({
      processedPeriods: 0,
      totalPeriods: conversionData.analysisResult?.sheets?.length || 0,
      currentPeriod: null,
      completed: false,
      mappedAccounts: 0,
      totalAccounts: 0,
      mappingProgress: 0
    });
    
    setTbConverterResults({
      period_identifier: `${conversionData.analysisResult?.sheets?.length || 0} Converted Periods`,
      accounts: [],
      accountsByPeriod: {},
      totalPeriods: conversionData.analysisResult?.sheets?.length || 0,
      statement_type: conversionData.selectedStatementType || 'income_statement',
      isTBConverter: true
    });
    
    try {
      await supabase.auth.getSession();
      
      // Build sheets config with period labels
      const sheetsConfig = conversionData.analysisResult.sheets.map((sheet, idx) => ({
        file_index: sheet.file_index,
        sheet_name: sheet.sheet_name,
        structure: sheet.structure,
        detected_period: sheet.detected_period,
        period_label: sheet.detected_period // Use detected period as label
      }));
      
      const formData = new FormData();
      conversionData.files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('sheets_config', JSON.stringify(sheetsConfig));
      formData.append('classifications', JSON.stringify(conversionData.classificationResult?.classifications_map || {}));
      formData.append('statement_type', conversionData.selectedStatementType || 'income_statement');
      
      // Pass config_id if a custom configuration was selected
      const configId = conversionData.selectedConfig?.id || '';
      if (configId) {
        formData.append('config_id', configId);
      }
      
      const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/tb-converter/stream-convert/`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Accept': 'text/event-stream',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
        throw new Error(errorData.detail || 'Server error occurred');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim() === '') continue;

            try {
              const parsedData = JSON.parse(data);
              
              if (parsedData.status === 'complete') {
                setLoading(false);
                setTbConverterProgress(prev => ({
                  ...prev,
                  processedPeriods: prev?.totalPeriods || 0,
                  completed: true,
                  currentPeriod: 'Conversion Complete'
                }));
              } else if (parsedData.status === 'extracting') {
                setTbConverterProgress(prev => ({
                  ...prev,
                  currentPeriod: parsedData.message || 'Extracting trial balance data...'
                }));
              } else if (parsedData.status === 'starting_optimized') {
                // AI mapping is starting
                setTbConverterProgress(prev => ({
                  ...prev,
                  totalPeriods: parsedData.total_periods,
                  totalAccounts: parsedData.total_accounts,
                  currentPeriod: parsedData.message || 'Starting AI mapping...'
                }));
              } else if (parsedData.status === 'starting_conversion') {
                setTbConverterProgress(prev => ({
                  ...prev,
                  currentPeriod: parsedData.message || 'Starting conversion...'
                }));
              } else if (parsedData.status === 'period_progress') {
                setTbConverterProgress(prev => ({
                  ...prev,
                  processedPeriods: parsedData.processed_periods,
                  currentPeriod: `Converting: ${parsedData.period}`,
                }));
              } else if (parsedData.status === 'mapping_progress') {
                setTbConverterProgress(prev => ({
                  ...prev,
                  currentPeriod: `Mapping: ${parsedData.account_name} → ${parsedData.category}`,
                  mappedAccounts: parsedData.completed || parsedData.progress?.completed,
                  totalAccounts: parsedData.total || parsedData.progress?.total,
                  mappingProgress: parsedData.progress_percentage
                }));
              } else if (parsedData.error) {
                throw new Error(parsedData.error);
              } else if (parsedData.original_name) {
                // This is an account - add it to results
                setTbConverterResults(prev => {
                  const newAccounts = [...prev.accounts, parsedData];
                  const periodKey = parsedData.period_display_name || parsedData.period;
                  
                  // Group accounts by period
                  const newAccountsByPeriod = { ...prev.accountsByPeriod };
                  if (!newAccountsByPeriod[periodKey]) {
                    newAccountsByPeriod[periodKey] = [];
                  }
                  newAccountsByPeriod[periodKey] = [...newAccountsByPeriod[periodKey], parsedData];
                  
                  return {
                    ...prev,
                    accounts: newAccounts,
                    accountsByPeriod: newAccountsByPeriod
                  };
                });
              }
            } catch (e) {
              logger.error('Error parsing JSON:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      handleError(err.message);
      setTbConverterProgress(prev => prev ? { ...prev, completed: true } : prev);
    } finally {
      setLoading(false);
    }
  };

  const handleTBConverterReset = () => {
    setTbConverterStep(1);
    setTbConverterData(null);
    setTbClassificationResult(null); // Clear classification on full reset
    setTbConverterResults(null);
    setTbConverterProgress(null);
    setError(null);
    setLoading(false);
  };

  const handleTBConverterBackToReview = () => {
    setTbConverterStep(2);
    // Preserve results and progress so user can navigate back via progress indicator
    setError(null);
    setLoading(false);
  };

  const handleTBConverterBackToClassification = () => {
    setTbConverterStep(3);
    // Preserve results and progress so user can navigate back via progress indicator
    setError(null);
    setLoading(false);
  };

  // ============================================================================
  // STEP NAVIGATORS — allow free navigation via clickable progress indicators
  // These only change the step without destroying data
  // ============================================================================

  const navigateMapperStep = (targetStep) => {
    if (targetStep === step || loading) return;
    if (targetStep === 1) { setStep(1); return; }
    if (targetStep === 2 && periodOptions) { setStep(2); return; }
    if (targetStep === 3 && results) { setStep(3); return; }
  };

  const navigateComparativeStep = (targetStep) => {
    if (targetStep === comparativeStep || loading) return;
    if (targetStep === 1) { setComparativeStep(1); return; }
    if (targetStep === 2 && comparativeData) { setComparativeStep(2); return; }
    if (targetStep === 3 && comparativeResults) { setComparativeStep(3); return; }
  };

  const navigateMergerStep = (targetStep) => {
    if (targetStep === mergerStep || loading) return;
    if (targetStep === 1) { setMergerStep(1); return; }
    if (targetStep === 2 && mergerData) { setMergerStep(2); return; }
    if (targetStep === 3 && mergerResults) { setMergerStep(3); return; }
  };

  const navigateTBConverterStep = (targetStep) => {
    if (targetStep === tbConverterStep || loading) return;
    if (targetStep === 1) { setTbConverterStep(1); return; }
    if (targetStep === 2 && tbConverterData) { setTbConverterStep(2); return; }
    if (targetStep === 3 && tbConverterData) { setTbConverterStep(3); return; }
    if (targetStep === 4 && tbConverterResults) { setTbConverterStep(4); return; }
  };

  return (
    <TenantProvider userEmail={currentUser}>
      <DashboardGate>
      <FeaturePageManager activePage={activePage} setActivePage={setActivePage} setHasComparativeFeature={setHasComparativeFeature} />
      <div className={`App ${loading && progress ? 'streaming' : ''}`}>
        <header className="App-header">
          <div className="header-content">
            <div className="logo">
              <img className="theme-logo" src="/logo-with-name.png" alt="Logo" />
            </div>
            
            <div className="header-actions">
              <div className="provider-selector">
                <label htmlFor="llm-provider" className="provider-label">AI:</label>
                <select
                  id="llm-provider"
                  className="provider-dropdown"
                  value={llmProvider}
                  onChange={handleProviderChange}
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                </select>
              </div>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </header>
        
        <div className={`App-body ${activePage === 'account' ? 'account-fullscreen' : ''}`}>
          {activePage !== 'account' && (
            <Sidebar
              activePage={activePage}
              onPageChange={handleSelectPage}
              savedSessions={savedSessions}
              sessionsLoading={sessionsLoading}
              mapperSessions={mapperSessions}
              mapperSessionsLoading={mapperSessionsLoading}
              agentSessions={agentSessions}
              onLoadSession={handleLoadSession}
              onLoadAgentSession={handleLoadAgentSession}
              onDeleteSession={handleDeleteSession}
              onDeleteAgentSession={handleDeleteAgentSession}
              onClearAllAgentSessions={handleClearAllAgentSessions}
              onPinMapperSession={handlePinMapperSession}
              onUnpinMapperSession={handleUnpinMapperSession}
              loadedSessionId={loadedSessionId}
              currentAgentSessionId={currentAgentSessionId}
              currentBalanceSheetSessionId={currentBalanceSheetSessionId}
              onLoadBalanceSheetSession={handleLoadBalanceSheetSession}
              currentTBToBSSessionId={currentTBToBSSessionId}
              onLoadTBToBSSession={handleLoadTBToBSSession}
              currentUnifiedSessionId={currentUnifiedSessionId}
              onLoadUnifiedSession={handleLoadUnifiedSession}
              currentDatabookSessionId={currentDatabookSessionId}
              onLoadDatabookSession={handleLoadDatabookSession}
              userEmail={currentUser}
              onOpenAccount={handleOpenAccount}
            />
          )}

          <main className="App-main">
            <div className="content-container">
              {/* Mapper Page */}
              {activePage === 'mapper' && (
                <div className="mapper-page">
                  {/* Progress indicator — hidden when viewing a saved session (no file/periods in memory to navigate back to) */}
                  {!isViewingSavedMapperSession && (
                    <div className="progress-indicator">
                      <div
                        className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''} ${step !== 1 ? 'clickable' : ''}`}
                        onClick={() => navigateMapperStep(1)}
                      >
                        <span className="step-number">1</span>
                        <span className="step-label">Upload File</span>
                      </div>
                      <div
                        className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''} ${periodOptions && step !== 2 ? 'clickable' : ''}`}
                        onClick={() => navigateMapperStep(2)}
                      >
                        <span className="step-number">2</span>
                        <span className="step-label">Select Periods</span>
                      </div>
                      <div
                        className={`step ${step >= 3 ? 'active' : ''} ${results && step !== 3 ? 'clickable' : ''}`}
                        onClick={() => navigateMapperStep(3)}
                      >
                        <span className="step-number">3</span>
                        <span className="step-label">View Results</span>
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="upload-section">
                      <FileUpload
                        onFileUpload={handleFileUpload}
                        onPeriodDiscovery={handlePeriodDiscovery}
                        onProcessStart={handleProcessStart}
                        onError={handleError}
                        loading={loading}
                        userEmail={currentUser}
                        hasComparativeFeature={hasComparativeFeature}
                      />
                    </div>
                  )}

                  {step === 2 && periodOptions && (
                    <div className="period-section">
                      {dataFormat === 'comparative' ? (
                        <ComparativeSummary
                          discoveryData={periodOptions}
                          onProcess={handleComparativeProcess}
                          onBack={handleReset}
                          loading={loading}
                          hasExistingResults={!!results}
                          onViewResults={() => setStep(3)}
                        />
                      ) : (
                        <PeriodSelectorMulti
                          fileData={fileData}
                          periodOptions={periodOptions}
                          onProcess={handleMultiPeriodStreamProcess}
                          onError={handleError}
                          onBack={handleReset}
                          loading={loading}
                          isDemoUser={false}
                          isAdminUser={currentUser?.includes('admin') || false}
                          hasExistingResults={!!results}
                          onViewResults={() => setStep(3)}
                        />
                      )}
                    </div>
                  )}

                  {step === 3 && sessionLoading && (
                    <div className="session-loading-container">
                      <div className="spinner"></div>
                      <p>Loading saved session...</p>
                    </div>
                  )}

                  {step === 3 && results && !sessionLoading && (
                    <div className="results-section">
                      <ResultsDisplayMulti
                        results={results}
                        onReset={handleReset}
                        onBack={handleBackToPeriods}
                        progress={progress}
                        netProfitCheck={netProfitCheck}
                        approvedSuggestions={approvedSuggestions}
                        setApprovedSuggestions={setApprovedSuggestions}
                        rejectedSuggestions={rejectedSuggestions}
                        setRejectedSuggestions={setRejectedSuggestions}
                        inputName={fileData?.file?.name}
                        inputSheet={fileData?.sheetName}
                        dataFormat={dataFormat}
                        isAdminUser={currentUser?.includes('admin') || false}
                        selectedTenantConfig={fileData?.selectedConfig}
                        qoeResults={mapperQoeResults}
                        setQoeResults={setMapperQoeResults}
                        showQoePanel={mapperQoeShowPanel}
                        setShowQoePanel={setMapperQoeShowPanel}
                        currentMapperSessionId={currentMapperSessionId}
                        onPinMapperSession={handlePinMapperSession}
                        onAttachQoeToMapperSession={handleAttachQoeToMapperSession}
                        onUpdateMapperSessionResults={handleUpdateMapperSessionResults}
                        isLoadedSession={isViewingSavedMapperSession}
                        processedAt={mapperProcessedAt}
                        originalSourceFileName={mapperSourceFileName}
                        onOpenWorkbench={
                          currentMapperSessionId
                            ? () => setActivePage('qoe-workbench')
                            : null
                        }
                      />
                    </div>
                  )}

                  {error && (
                    <div className="error-container">
                      <h3>Error</h3>
                      <p>{error}</p>
                      <button onClick={handleReset} className="reset-button">
                        Start Over
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Comparative Page - Dedicated standalone comparative analysis */}
              {activePage === 'comparative' && (
                <div className="mapper-page">
                  {/* Progress indicator */}
                  <div className="progress-indicator">
                    <div 
                      className={`step ${comparativeStep >= 1 ? 'active' : ''} ${comparativeStep > 1 ? 'completed' : ''} ${comparativeStep !== 1 ? 'clickable' : ''}`}
                      onClick={() => navigateComparativeStep(1)}
                    >
                      <span className="step-number">1</span>
                      <span className="step-label">Upload File</span>
                    </div>
                    <div 
                      className={`step ${comparativeStep >= 2 ? 'active' : ''} ${comparativeStep > 2 ? 'completed' : ''} ${comparativeData && comparativeStep !== 2 ? 'clickable' : ''}`}
                      onClick={() => navigateComparativeStep(2)}
                    >
                      <span className="step-number">2</span>
                      <span className="step-label">Align Periods</span>
                    </div>
                    <div 
                      className={`step ${comparativeStep >= 3 ? 'active' : ''} ${comparativeResults && comparativeStep !== 3 ? 'clickable' : ''}`}
                      onClick={() => navigateComparativeStep(3)}
                    >
                      <span className="step-number">3</span>
                      <span className="step-label">View Results</span>
                    </div>
                  </div>

                  {comparativeStep === 1 && (
                    <div className="upload-section">
                      <ComparativeFileUpload
                        onFileUpload={(file, sheetName, statementType, dataFormat) => {
                          setFileData({ file, sheetName });
                          setStatementType(statementType);
                          setDataFormat(dataFormat);
                          setComparativeStep(2);
                          setError(null);
                          // Clear stale results from previous file
                          setComparativeResults(null);
                          setComparativeProgress(null);
                          setComparativeEbitdaChecks({});
                          setLoadedSessionId(null);
                        }}
                        onPeriodDiscovery={(options, dataFormat) => {
                          setComparativeData(options);
                          setDataFormat(dataFormat);
                          setLoading(false);
                          setError(null);
                        }}
                        onProcessStart={handleProcessStart}
                        onError={handleError}
                        loading={loading}
                      />
                    </div>
                  )}

                  {comparativeStep === 2 && comparativeData && (
                    <div className="period-section">
                      <ComparativeSummary
                        discoveryData={comparativeData}
                        hasExistingResults={!!comparativeResults}
                        onViewResults={() => setComparativeStep(3)}
                        onProcess={async (commonPeriods) => {
                          // Start SEQUENTIAL RAW DATA + PARALLEL COMPARABLES processing
                          logger.log('🚀 Starting comparative processing for periods:', commonPeriods);
                          handleProcessStart();
                          setComparativeStep(3);
                          
                          const file = comparativeData.file || fileData?.file;
                          const companyContext = comparativeData.rawData?.company_context || '';
                          const comparables = comparativeData.comparables;
                          
                          // Initialize sections for progress tracking
                          const sections = [
                            { id: 'raw_data', name: `Raw Data: ${comparativeData.rawSheetName}`, processed: 0, total: 0, status: 'active' },
                            ...comparables.map((comp, i) => ({
                              id: `comp_${i}`,
                              name: `Comparable: ${comp.sheet}`,
                              processed: 0,
                              total: 0,
                              status: 'pending'
                            }))
                          ];
                          
                          // Track completed streams
                          let completedStreams = 0;
                          const totalStreams = 1 + comparables.length;
                          
                          setComparativeProgress({
                            processedPeriods: 0,
                            totalPeriods: commonPeriods.length,
                            currentPeriod: 'Processing Raw Data...',
                            completed: false,
                            mappedAccounts: 0,
                            totalAccounts: 0,
                            mappingProgress: 0,
                            sections: sections,
                            completedStreams: 0,
                            totalStreams: totalStreams
                          });
                          
                          // Convert source file to base64 for referencing mode
                          let sourceFileBase64 = null;
                          try {
                            const fileArrayBuffer = await file.arrayBuffer();
                            const bytes = new Uint8Array(fileArrayBuffer);
                            let binary = '';
                            bytes.forEach(byte => binary += String.fromCharCode(byte));
                            sourceFileBase64 = btoa(binary);
                          } catch (e) {
                            console.warn('Could not convert source file to base64:', e);
                          }
                          
                          // Build comparable metadata for referencing
                          const comparableMetadataInline = {};
                          const filterPeriodsForMetaInline = (periodOpts, sheetName) => {
                            return (periodOpts || []).filter(period => {
                              const displayName = period.display_name || '';
                              return commonPeriods.some(cp => {
                                if (typeof cp === 'object') {
                                  const originalNames = cp.original_names || {};
                                  return originalNames[sheetName] === displayName || cp.common_name === displayName;
                                }
                                return displayName === String(cp);
                              });
                            });
                          };
                          comparables.forEach(comp => {
                            const filteredPeriods = filterPeriodsForMetaInline(comp.data.period_options || [], comp.sheet);
                            const periodColumnMap = {};
                            filteredPeriods.forEach(p => {
                              if (p.period_index !== undefined && p.column_index !== undefined) {
                                periodColumnMap[p.period_index] = p.column_index;
                              }
                            });
                            comparableMetadataInline[comp.sheet] = {
                              header_end_row: comp.data.header_end_row || 0,
                              data_start_row: comp.data.data_start_row ?? null,
                              period_column_map: periodColumnMap
                            };
                          });
                          
                          setComparativeResults({
                            period_identifier: `${commonPeriods.length} Common Periods`,
                            accounts: [],
                            accountsByEntity: {},
                            totalPeriods: commonPeriods.length,
                            statement_type: 'comparative_income_statement',
                            company_context: companyContext,
                            // Store for referencing feature
                            source_file_base64: sourceFileBase64,
                            raw_data_sheet_name: comparativeData.rawSheetName,
                            comparable_metadata: comparableMetadataInline
                          });
                          
                          logger.log('📤 COMPARATIVE PROCESSING ORDER:');
                          logger.log('  1️⃣ Raw sheet (first):', comparativeData.rawSheetName);
                          logger.log('  2️⃣ Comparables (parallel after raw):', comparables.map(c => c.sheet));
                          logger.log('  📊 Common periods:', commonPeriods.length);
                          
                          // Helper to process a single SSE stream
                          const processStream = async (response, sectionId, sourceType, sheetName) => {
                            const reader = response.body.getReader();
                            const decoder = new TextDecoder();
                            let buffer = '';
                            
                            while (true) {
                              const { done, value } = await reader.read();
                              if (done) break;
                              
                              buffer += decoder.decode(value, { stream: true });
                              const lines = buffer.split('\n');
                              buffer = lines.pop() || '';
                              
                              for (const line of lines) {
                                if (line.startsWith('data: ')) {
                                  const data = line.slice(6);
                                  if (data.trim() === '') continue;
                                  
                                  try {
                                    const parsedData = JSON.parse(data);
                                    
                                    if (parsedData.status === 'complete' || parsedData.status === 'starting_raw_data' || parsedData.status === 'starting_optimized') {
                                      if (parsedData.status === 'complete') {
                                        setComparativeProgress(prev => ({
                                          ...prev,
                                          sections: prev.sections.map(s => 
                                            s.id === sectionId 
                                              ? { ...s, status: 'completed', processed: parsedData.unique_accounts || parsedData.total_accounts || s.processed }
                                              : s
                                          )
                                        }));
                                      }
                                    } else if (parsedData.status === 'mapping_progress') {
                                      setComparativeProgress(prev => ({
                                        ...prev,
                                        currentPeriod: `[${sheetName}] Mapping: ${parsedData.account_name} → ${parsedData.category}`,
                                        sections: prev.sections.map(s => 
                                          s.id === sectionId 
                                            ? { ...s, processed: parsedData.completed, total: parsedData.total }
                                            : s
                                        )
                                      }));
                                    } else if (parsedData.status === 'ebitda_check_started') {
                                      // EBITDA reconciliation starting for this comparable sheet
                                      setComparativeProgress(prev => ({
                                        ...prev,
                                        currentPeriod: `[${sheetName}] Checking EBITDA reconciliation...`,
                                      }));
                                      setComparativeEbitdaChecks(prev => ({
                                        ...prev,
                                        [sheetName]: { phase: 'checking', corrections: [], result: null }
                                      }));
                                    } else if (parsedData.status === 'account_correction') {
                                      // Account corrected by EBITDA checker — update results
                                      const corrected = parsedData.account;
                                      const oldCategory = parsedData.old_category || 'Unknown';
                                      
                                      setComparativeEbitdaChecks(prev => {
                                        const existing = prev[sheetName] || { phase: 'checking', corrections: [], result: null };
                                        return {
                                          ...prev,
                                          [sheetName]: {
                                            ...existing,
                                            corrections: [...existing.corrections, {
                                              original_name: corrected.original_name,
                                              old_category: oldCategory,
                                              new_category: corrected.standardized_category,
                                              reasoning: corrected.notes?.replace(/\[Remapped by EBITDA Checker\] /, '') || '',
                                            }]
                                          }
                                        };
                                      });
                                      
                                      // Update account in comparative results
                                      const accountWithMeta = {
                                        ...corrected,
                                        source_type: sourceType,
                                        source_sheet: sheetName,
                                        entity: corrected.entity || sheetName
                                      };
                                      setComparativeResults(prev => {
                                        if (!prev) return prev;
                                        const updatedAccounts = prev.accounts.map(acct =>
                                          (acct.original_name === corrected.original_name && acct.period === corrected.period && (acct.source_sheet || acct.entity) === sheetName)
                                            ? { ...acct, ...accountWithMeta }
                                            : acct
                                        );
                                        return { ...prev, accounts: updatedAccounts };
                                      });
                                    } else if (parsedData.status === 'ebitda_check') {
                                      // EBITDA check completed for this sheet
                                      const checkStatus = parsedData.check_status || 'UNKNOWN';
                                      setComparativeEbitdaChecks(prev => ({
                                        ...prev,
                                        [sheetName]: {
                                          ...(prev[sheetName] || {}),
                                          phase: 'done',
                                          result: {
                                            status: checkStatus,
                                            reconciliation_summary: parsedData.reconciliation_summary,
                                            all_applied_corrections: parsedData.all_applied_corrections,
                                            period_results: parsedData.period_results,
                                          },
                                        }
                                      }));
                                    } else if (parsedData.error) {
                                      logger.error(`Error in stream ${sectionId}:`, parsedData.error);
                                      throw new Error(parsedData.error);
                                    } else if (parsedData.original_name) {
                                      const accountWithMeta = {
                                        ...parsedData,
                                        source_type: parsedData.source_type || sourceType,
                                        source_sheet: parsedData.source_sheet || sheetName,
                                        entity: parsedData.entity || sheetName
                                      };
                                      
                                      setComparativeResults(prev => {
                                        const accounts = [...(prev.accounts || []), accountWithMeta];
                                        const accountsByEntity = { ...(prev.accountsByEntity || {}) };
                                        const entity = accountWithMeta.entity || 'Unknown';
                                        if (!accountsByEntity[entity]) {
                                          accountsByEntity[entity] = [];
                                        }
                                        accountsByEntity[entity].push(accountWithMeta);
                                        return { ...prev, accounts, accountsByEntity };
                                      });
                                    }
                                  } catch (parseError) {
                                    logger.error('Error parsing SSE data:', parseError);
                                  }
                                }
                              }
                            }
                            
                            completedStreams++;
                            logger.log(`✅ Stream ${sectionId} completed (${completedStreams}/${totalStreams})`);
                            setComparativeProgress(prev => ({
                              ...prev,
                              completedStreams: completedStreams
                            }));
                          };
                          
                          // Filter period_options to only include common periods
                          const filterPeriodsToCommon = (periodOptions, sheetName) => {
                            return periodOptions.filter(period => {
                              const displayName = period.display_name || '';
                              return commonPeriods.some(cp => {
                                if (typeof cp === 'object') {
                                  const originalNames = cp.original_names || {};
                                  return originalNames[sheetName] === displayName || cp.common_name === displayName;
                                }
                                return displayName === String(cp);
                              });
                            });
                          };
                          
                          try {
                            // === STEP 1: PROCESS RAW DATA FIRST ===
                            logger.log('1️⃣ Starting Raw Data processing...');
                            
                            const rawFormData = new FormData();
                            rawFormData.append('file', file);
                            rawFormData.append('sheet_name', comparativeData.rawSheetName);
                            rawFormData.append('entity_column', 'Entity');
                            rawFormData.append('account_column', 'Account');
                            rawFormData.append('date_column', 'Date');
                            rawFormData.append('value_column', 'Value');
                            rawFormData.append('common_periods', JSON.stringify(commonPeriods));
                            rawFormData.append('company_context', companyContext);
                            
                            const rawResponse = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/stream-process-raw-data/`, {
                              method: 'POST',
                              body: rawFormData,
                              credentials: 'include',
                              headers: {
                                'Accept': 'text/event-stream',
                                'x-api-key': import.meta.env.VITE_API_KEY,
                              },
                            });
                            
                            if (!rawResponse.ok) {
                              const errorText = await rawResponse.text();
                              throw new Error(`Raw data request failed: ${rawResponse.status} - ${errorText}`);
                            }
                            
                            // Process raw data stream and wait for completion
                            await processStream(rawResponse, 'raw_data', 'entity', comparativeData.rawSheetName);
                            logger.log('✅ Raw Data processing complete!');
                            
                            // === STEP 2: PROCESS COMPARABLES IN PARALLEL ===
                            if (comparables.length > 0) {
                              logger.log(`2️⃣ Starting ${comparables.length} Comparable sheets in parallel...`);
                              
                              // Update progress to show comparables starting
                              setComparativeProgress(prev => ({
                                ...prev,
                                currentPeriod: 'Processing Comparable sheets...',
                                sections: prev.sections.map(s => 
                                  s.id.startsWith('comp_') ? { ...s, status: 'active' } : s
                                )
                              }));
                              
                              const comparablePromises = comparables.map(async (comp, idx) => {
                                const compFormData = new FormData();
                                compFormData.append('file', file);
                                compFormData.append('sheet_name', comp.sheet);
                                
                                const filteredPeriods = filterPeriodsToCommon(comp.data.period_options || [], comp.sheet);
                                compFormData.append('period_options', JSON.stringify(filteredPeriods));
                                
                                compFormData.append('account_name_column', String(comp.data.account_name_column || 0));
                                compFormData.append('header_start_row', String(comp.data.header_start_row || 0));
                                compFormData.append('header_end_row', String(comp.data.header_end_row || 0));
                                if (comp.data.data_start_row != null) {
                                  compFormData.append('data_start_row', comp.data.data_start_row.toString());
                                }
                                if (comp.data.data_end_row != null) {
                                  compFormData.append('data_end_row', comp.data.data_end_row.toString());
                                }
                                compFormData.append('company_context', companyContext);
                                compFormData.append('statement_type', 'comparative_income_statement');
                                
                                const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/stream-classify-accounts/`, {
                                  method: 'POST',
                                  body: compFormData,
                                  credentials: 'include',
                                  headers: {
                                    'Accept': 'text/event-stream',
                                    'x-api-key': import.meta.env.VITE_API_KEY,
                                  },
                                });
                                
                                if (!response.ok) {
                                  throw new Error(`Comparable ${comp.sheet} request failed: ${response.status}`);
                                }
                                
                                return processStream(response, `comp_${idx}`, 'comparable', comp.sheet);
                              });
                              
                              // Wait for all comparables to complete
                              await Promise.all(comparablePromises);
                              logger.log('✅ All Comparable sheets processed!');
                            }
                            
                            // === ALL PROCESSING COMPLETE ===
                            logger.log('🎉 All comparative processing complete!');
                            setLoading(false);
                            setComparativeProgress(prev => ({
                              ...prev,
                              completed: true,
                              currentPeriod: 'All processing complete!',
                              completedStreams: totalStreams
                            }));

                            // Auto-save session (fire-and-forget, read from ref for latest state)
                            saveComparativeSession(comparativeResultsRef.current).catch(e => logger.error('Auto-save failed:', e));

                          } catch (error) {
                            if (error instanceof SessionExpiredError) return;
                            logger.error('Comparative processing error:', error);
                            setError(error.message || 'Comparative processing failed');
                            setLoading(false);
                          }
                        }}
                        onBack={() => {
                          setComparativeStep(1);
                          setComparativeData(null);
                        }}
                        loading={loading}
                      />
                    </div>
                  )}

                  {comparativeStep === 3 && sessionLoading && (
                    <div className="session-loading-container">
                      <div className="spinner"></div>
                      <p>Loading saved session...</p>
                    </div>
                  )}

                  {comparativeStep === 3 && comparativeResults && !sessionLoading && (
                    <div className="results-section">
                      <ResultsDisplayMulti
                        results={comparativeResults}
                        progress={comparativeProgress}
                        netProfitCheck={netProfitCheck}
                        approvedSuggestions={approvedSuggestions}
                        setApprovedSuggestions={setApprovedSuggestions}
                        rejectedSuggestions={rejectedSuggestions}
                        setRejectedSuggestions={setRejectedSuggestions}
                        onReset={() => {
                          setComparativeStep(1);
                          setComparativeData(null);
                          setComparativeResults(null);
                          setComparativeProgress(null);
                          setComparativeEbitdaChecks({});
                          setLoadedSessionId(null);
                          setError(null);
                          setLoading(false);
                        }}
                        onBack={() => {
                          setComparativeStep(2);
                          // Preserve results and progress so user can navigate back via progress indicator
                          setError(null);
                          setLoading(false);
                        }}
                        inputName={fileData?.file?.name}
                        dataFormat="comparative"
                        ebitdaChecks={comparativeEbitdaChecks}
                      />
                    </div>
                  )}

                  {error && (
                    <div className="error-container">
                      <h3>Error</h3>
                      <p>{error}</p>
                      <button onClick={() => {
                        setComparativeStep(1);
                        setComparativeData(null);
                        setComparativeResults(null);
                        setComparativeProgress(null);
                        setLoadedSessionId(null);
                        setError(null);
                        setLoading(false);
                      }} className="reset-button">
                        Start Over
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Merger Page */}
              {activePage === 'merger' && (
                <div className="mapper-page">
                  {/* Progress indicator */}
                  <div className="progress-indicator">
                    <div 
                      className={`step ${mergerStep >= 1 ? 'active' : ''} ${mergerStep > 1 ? 'completed' : ''} ${mergerStep !== 1 ? 'clickable' : ''}`}
                      onClick={() => navigateMergerStep(1)}
                    >
                      <span className="step-number">1</span>
                      <span className="step-label">Upload Files</span>
                    </div>
                    <div 
                      className={`step ${mergerStep >= 2 ? 'active' : ''} ${mergerStep > 2 ? 'completed' : ''} ${mergerData && mergerStep !== 2 ? 'clickable' : ''}`}
                      onClick={() => navigateMergerStep(2)}
                    >
                      <span className="step-number">2</span>
                      <span className="step-label">Select Periods</span>
                    </div>
                    <div 
                      className={`step ${mergerStep >= 3 ? 'active' : ''} ${mergerResults && mergerStep !== 3 ? 'clickable' : ''}`}
                      onClick={() => navigateMergerStep(3)}
                    >
                      <span className="step-number">3</span>
                      <span className="step-label">View Results</span>
                    </div>
                  </div>

                  {mergerStep === 1 && (
                    <div className="upload-section">
                      <MergerUpload
                        onDiscoveryComplete={handleMergerDiscoveryComplete}
                        onProcessStart={handleProcessStart}
                        onError={handleError}
                        loading={loading}
                        userEmail={currentUser}
                      />
                    </div>
                  )}

                  {mergerStep === 2 && mergerData && (
                    <div className="period-section">
                      <MergerPeriodSelector
                        mergerData={mergerData}
                        onProcess={handleMergerProcess}
                        onError={handleError}
                        onBack={handleMergerReset}
                        loading={loading}
                        hasExistingResults={!!mergerResults}
                        onViewResults={() => setMergerStep(3)}
                      />
                    </div>
                  )}

                  {mergerStep === 3 && mergerResults && (
                    <div className="results-section">
                      <ResultsDisplayMulti
                        results={mergerResults}
                        onReset={handleMergerReset}
                        onBack={handleMergerBackToPeriods}
                        progress={mergerProgress}
                        netProfitCheck={netProfitCheck}
                        approvedSuggestions={approvedSuggestions}
                        setApprovedSuggestions={setApprovedSuggestions}
                        rejectedSuggestions={rejectedSuggestions}
                        setRejectedSuggestions={setRejectedSuggestions}
                        inputName="Merged P&L"
                        inputSheet={`${mergerData?.sheetSelections?.length || 0} sheets`}
                        dataFormat="pl"
                        isAdminUser={currentUser?.includes('admin') || false}
                        isMerger={true}
                        selectedTenantConfig={mergerResults?.selectedConfig || mergerData?.selectedConfig}
                        mergerSheetSources={mergerData?.discoveryResult?.sheet_sources}
                        qoeResults={mergerQoeResults}
                        setQoeResults={setMergerQoeResults}
                        showQoePanel={mergerQoeShowPanel}
                        setShowQoePanel={setMergerQoeShowPanel}
                      />
                    </div>
                  )}

                  {error && (
                    <div className="error-container">
                      <h3>Error</h3>
                      <p>{error}</p>
                      <button onClick={handleMergerReset} className="reset-button">
                        Start Over
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Recon Page */}
              {activePage === 'recon' && (
                <div className="recon-page">
                  <ReconUpload
                    onError={handleError}
                    loading={loading}
                    userEmail={currentUser}
                  />
                  
                  {error && (
                    <div className="error-container">
                      <h3>Error</h3>
                      <p>{error}</p>
                      <button onClick={() => setError(null)} className="reset-button">
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* TB Converter Page */}
              {activePage === 'tb-converter' && (
                <div className="mapper-page">
                  {/* Progress indicator */}
                  <div className="progress-indicator">
                    <div 
                      className={`step ${tbConverterStep >= 1 ? 'active' : ''} ${tbConverterStep > 1 ? 'completed' : ''} ${tbConverterStep !== 1 ? 'clickable' : ''}`}
                      onClick={() => navigateTBConverterStep(1)}
                    >
                      <span className="step-number">1</span>
                      <span className="step-label">Upload TBs</span>
                    </div>
                    <div 
                      className={`step ${tbConverterStep >= 2 ? 'active' : ''} ${tbConverterStep > 2 ? 'completed' : ''} ${tbConverterData && tbConverterStep !== 2 ? 'clickable' : ''}`}
                      onClick={() => navigateTBConverterStep(2)}
                    >
                      <span className="step-number">2</span>
                      <span className="step-label">Review Periods</span>
                    </div>
                    <div 
                      className={`step ${tbConverterStep >= 3 ? 'active' : ''} ${tbConverterStep > 3 ? 'completed' : ''} ${tbConverterData && tbConverterStep !== 3 ? 'clickable' : ''}`}
                      onClick={() => navigateTBConverterStep(3)}
                    >
                      <span className="step-number">3</span>
                      <span className="step-label">Classify Accounts</span>
                    </div>
                    <div 
                      className={`step ${tbConverterStep >= 4 ? 'active' : ''} ${tbConverterResults && tbConverterStep !== 4 ? 'clickable' : ''}`}
                      onClick={() => navigateTBConverterStep(4)}
                    >
                      <span className="step-number">4</span>
                      <span className="step-label">View Results</span>
                    </div>
                  </div>

                  {tbConverterStep === 1 && (
                    <div className="upload-section">
                      <TBConverterUpload
                        onAnalysisComplete={handleTBAnalysisComplete}
                        onProcessStart={handleProcessStart}
                        onError={handleError}
                        loading={loading}
                        userEmail={currentUser}
                      />
                    </div>
                  )}

                  {tbConverterStep === 2 && tbConverterData && (
                    <div className="period-section">
                      <TBConverterPeriodReview
                        analysisData={tbConverterData}
                        onContinue={handleTBPeriodReviewComplete}
                        onBack={handleTBConverterReset}
                        loading={loading}
                        hasExistingResults={!!tbConverterResults}
                        onViewResults={() => setTbConverterStep(4)}
                      />
                    </div>
                  )}

                  {tbConverterStep === 3 && tbConverterData && (
                    <div className="classification-section">
                      <TBConverterClassification
                        analysisData={tbConverterData}
                        existingClassification={tbClassificationResult}
                        onClassificationComplete={handleTBClassificationComplete}
                        onBack={handleTBConverterBackToReview}
                        loading={loading}
                        onProcessStart={handleProcessStart}
                        onError={handleError}
                        hasExistingResults={!!tbConverterResults}
                        onViewResults={() => setTbConverterStep(4)}
                      />
                    </div>
                  )}

                  {tbConverterStep === 4 && tbConverterResults && (
                    <div className="results-section">
                      <ResultsDisplayMulti
                        results={tbConverterResults}
                        onReset={handleTBConverterReset}
                        onBack={handleTBConverterBackToClassification}
                        progress={tbConverterProgress}
                        netProfitCheck={netProfitCheck}
                        approvedSuggestions={approvedSuggestions}
                        setApprovedSuggestions={setApprovedSuggestions}
                        rejectedSuggestions={rejectedSuggestions}
                        setRejectedSuggestions={setRejectedSuggestions}
                        inputName="Converted Trial Balance"
                        inputSheet={`${tbConverterData?.sheetSelections?.length || 0} sheets`}
                        dataFormat="pl"
                        isAdminUser={currentUser?.includes('admin') || false}
                        isTBConverter={true}
                        selectedTenantConfig={tbConverterData?.selectedConfig}
                      />
                    </div>
                  )}

                  {error && (
                    <div className="error-container">
                      <h3>Error</h3>
                      <p>{error}</p>
                      <button onClick={handleTBConverterReset} className="reset-button">
                        Start Over
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Chart of Accounts Page */}
              {activePage === 'chart-of-accounts' && (
                <div className="chart-of-accounts-page">
                  <ChartOfAccountsEditor 
                    userId={session?.user?.id}
                    userEmail={currentUser}
                    tenantName={tenantInfo?.name}
                    tenantSlug={tenantInfo?.slug}
                  />
                </div>
              )}

              {/* QoE Workbench Page */}
              {activePage === 'qoe-workbench' && (
                <div className="qoe-workbench-page">
                  <QoEWorkbench
                    mappingSessionId={currentMapperSessionId}
                    sessionName={
                      mapperSessions.find((s) => s.id === currentMapperSessionId)?.name || null
                    }
                    mapperSessions={mapperSessions}
                    mapperSessionsLoading={mapperSessionsLoading}
                    onSelectSession={(sessionId) => setCurrentMapperSessionId(sessionId)}
                    onClearSession={() => setCurrentMapperSessionId(null)}
                  />
                </div>
              )}

              {/* Agent Page */}
              {activePage === 'agent' && (
                <div className="agent-page">
                  <Agent
                    userEmail={currentUser}
                    initialSessionId={currentAgentSessionId}
                    onSessionIdChange={(id) => {
                      setCurrentAgentSessionId(id);
                      // Refresh sidebar list whenever a new session starts or
                      // is dismissed, so the user sees their latest run.
                      fetchAgentSessions();
                    }}
                    // Re-fetch when the run's first event lands. The
                    // onSessionIdChange refresh above fires at session-create,
                    // BEFORE the upload writes the Postgres row that
                    // GET /agent/sessions reads — so it misses the new row.
                    // This second nudge fires once the run is streaming (row
                    // now exists), so the session appears live and seeds the
                    // 5s status poll.
                    onSessionsRefresh={fetchAgentSessions}
                  />
                </div>
              )}

              {/* Balance Sheet Agent Page — isolated BS flow (mode='bs') */}
              {activePage === 'balance-sheet' && (
                <div className="agent-page">
                  <BalanceSheetAgent
                    userEmail={currentUser}
                    initialSessionId={currentBalanceSheetSessionId}
                    onSessionIdChange={(id) => {
                      setCurrentBalanceSheetSessionId(id);
                      fetchAgentSessions();
                    }}
                    onSessionsRefresh={fetchAgentSessions}
                  />
                </div>
              )}

              {/* Trial Balance → Balance Sheet Agent Page — isolated tb_bs flow (mode='tb_bs') */}
              {activePage === 'tb-to-bs' && (
                <div className="agent-page">
                  <TrialBalanceToBalanceSheetAgent
                    userEmail={currentUser}
                    initialSessionId={currentTBToBSSessionId}
                    onSessionIdChange={(id) => {
                      setCurrentTBToBSSessionId(id);
                      fetchAgentSessions();
                    }}
                    onSessionsRefresh={fetchAgentSessions}
                  />
                </div>
              )}

              {/* Full Statements Page — unified P&L + BS flow (parent session
                  chaining the two existing agents server-side) */}
              {activePage === 'unified' && (
                <div className="agent-page">
                  <UnifiedAgent
                    initialSessionId={currentUnifiedSessionId}
                    onSessionIdChange={(id) => {
                      setCurrentUnifiedSessionId(id);
                      fetchAgentSessions();
                    }}
                    onSessionsRefresh={fetchAgentSessions}
                  />
                </div>
              )}

              {/* Databook Page: many workbooks in, the client's databook out
                  (parent session chaining the two agents, as unified) */}
              {activePage === 'databook' && (
                <div className="agent-page">
                  <DatabookAgent
                    initialSessionId={currentDatabookSessionId}
                    onSessionIdChange={(id) => {
                      setCurrentDatabookSessionId(id);
                      fetchAgentSessions();
                    }}
                    onSessionsRefresh={fetchAgentSessions}
                  />
                </div>
              )}

              {/* Account Overview Page */}
              {activePage === 'account' && (
                <div className="account-page">
                  <AccountOverview
                    userEmail={currentUser}
                    onTenantSetup={handleTenantSetup}
                    isDarkMode={isDarkMode}
                    toggleTheme={toggleTheme}
                    onBack={handleCloseAccount}
                  />
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Session Expired Re-Auth Modal — overlays on top of app, preserves all state */}
      {sessionExpired && (
        <SessionExpiredModal
          userEmail={currentUser}
          onReauthenticate={handleReauthenticate}
        />
      )}
      </DashboardGate>

      {/* Toast notification */}
      {toast && toast.visible && (
        <div className="toast-notification">
          <span className="toast-check">&#10003;</span>
          {toast.message}
        </div>
      )}
    </TenantProvider>
  );
}

export default App;
